import {
  type ApprovalRequestId,
  DEFAULT_MODEL_BY_PROVIDER,
  type EditorId,
  type KeybindingCommand,
  type CodexReasoningEffort,
  type MessageId,
  type ProjectId,
  type ProjectEntry,
  type ProjectScript,
  type ModelSlug,
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
  type ResolvedKeybindingsConfig,
  type ProviderApprovalDecision,
  type ServerProviderStatus,
  type ProviderKind,
  type ThreadId,
  type TurnId,
  type OrchestrationLatestTurn,
  type OrchestrationLatestTurnState,
  OrchestrationThreadActivity,
  type RuntimeMode,
  ProviderInteractionMode,
} from "@t3tools/contracts";
import {
  getDefaultModel,
  getDefaultReasoningEffort,
  getRecommendedReasoningEffort,
  getReasoningEffortOptions,
  isLegacyModelSlug,
  normalizeModelSlug,
  resolveModelSlugForProvider,
} from "@t3tools/shared/model";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { gitBranchesQueryOptions, gitCreateWorktreeMutationOptions } from "~/lib/gitReactQuery";
import { projectSearchEntriesQueryOptions } from "~/lib/projectReactQuery";
import { serverConfigQueryOptions, serverQueryKeys } from "~/lib/serverReactQuery";
import { isElectron } from "../env";
import { parseDiffRouteSearch, stripDiffSearchParams } from "../diffRouteSearch";
import {
  clampCollapsedComposerCursor,
  type ComposerTrigger,
  collapseExpandedComposerCursor,
  detectComposerTrigger,
  expandCollapsedComposerCursor,
  parseComposerSkillInvocations,
  parseStandaloneComposerSlashCommand,
  replaceTextRange,
} from "../composer-logic";
import {
  derivePendingApprovals,
  derivePendingUserInputs,
  derivePhase,
  deriveTimelineEntries,
  deriveActiveWorkStartedAt,
  deriveActivePlanState,
  deriveCompletedTurnCollapseStates,
  findFirstTimelineEntryIdForTurnContent,
  findLatestProposedPlan,
  deriveWorkLogEntries,
  hasToolActivityForTurn,
  isLatestTurnSettled,
  formatElapsed,
  deriveTimelineActivePromptAnchor,
} from "../session-logic";
import { isScrollContainerNearBottom } from "../chat-scroll";
import {
  buildPendingUserInputAnswers,
  derivePendingUserInputProgress,
  setPendingUserInputCustomAnswer,
  type PendingUserInputDraftAnswer,
} from "../pendingUserInput";
import { useStore } from "../store";
import {
  buildPlanImplementationThreadTitle,
  buildPlanImplementationPrompt,
  proposedPlanTitle,
  resolvePlanFollowUpSubmission,
} from "../proposedPlan";
import { truncateTitle } from "../truncateTitle";
import {
  DEFAULT_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  DEFAULT_THREAD_TERMINAL_ID,
  MAX_TERMINALS_PER_GROUP,
  type ChatMessage,
  type TurnDiffSummary,
} from "../types";
import { basenameOfPath } from "../vscode-icons";
import { useTheme } from "../hooks/useTheme";
import { useTurnDiffSummaries } from "../hooks/useTurnDiffSummaries";
import BranchToolbar from "./BranchToolbar";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import PlanSidebar from "./PlanSidebar";
import ThreadTerminalDrawer from "./ThreadTerminalDrawer";
import {
  BotIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  FileIcon,
  LoaderCircleIcon,
  ListTodoIcon,
  MicIcon,
  PaperclipIcon,
  XIcon,
} from "lucide-react";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { cn, randomUUID } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { toastManager } from "./ui/toast";
import { decodeProjectScriptKeybindingRule } from "~/lib/projectScriptKeybindings";
import { type NewProjectScriptInput } from "./ProjectScriptsControl";
import {
  commandForProjectScript,
  nextProjectScriptId,
  projectScriptRuntimeEnv,
  projectScriptIdFromCommand,
  setupProjectScript,
} from "~/projectScripts";
import { SidebarCollapsedTrigger } from "./ui/sidebar";
import { newCommandId, newMessageId, newThreadId } from "~/lib/utils";
import { readNativeApi } from "~/nativeApi";
import {
  resolveAppModelSelection,
  resolveAppModelSelectionWithReasoning,
  resolveNewThreadModel,
  operatorsSurfaceModel,
  useAppSettings,
} from "../appSettings";
import { isTerminalFocused } from "../lib/terminalFocus";
import {
  getCachedOperatorsSession,
  operatorsSessionQueryOptions,
} from "../lib/operatorsReactQuery";
import {
  operatorsSkillQueryKeys,
  operatorsSessionSkillsQueryOptions,
  operatorsSkillsQueryOptions,
} from "../lib/operatorsSkillsReactQuery";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type DraftThreadEnvMode,
  type PersistedComposerImageAttachment,
  useComposerDraftStore,
  useComposerThreadDraft,
} from "../composerDraftStore";
import { shouldUseCompactComposerFooter } from "./composerFooterLayout";
import { selectThreadTerminalState, useTerminalStateStore } from "../terminalStateStore";
import { ComposerPromptEditor, type ComposerPromptEditorHandle } from "./ComposerPromptEditor";
import { PullRequestThreadDialog } from "./PullRequestThreadDialog";
import { MessagesTimeline } from "./chat/MessagesTimeline";
import { ChatHeader } from "./chat/ChatHeader";
import { OperatorsSurfaceToggle } from "./chat/OperatorsSurfaceToggle";
import { buildExpandedImagePreview, ExpandedImagePreview } from "./chat/ExpandedImagePreview";
import { AVAILABLE_PROVIDER_OPTIONS, ProviderModelPicker } from "./chat/ProviderModelPicker";
import { ComposerCommandItem, ComposerCommandMenu } from "./chat/ComposerCommandMenu";
import { ComposerPendingApprovalActions } from "./chat/ComposerPendingApprovalActions";
import { CodexTraitsPicker } from "./chat/CodexTraitsPicker";
import { CompactComposerControlsMenu } from "./chat/CompactComposerControlsMenu";
import { ComposerPendingApprovalPanel } from "./chat/ComposerPendingApprovalPanel";
import { ComposerPendingUserInputPanel } from "./chat/ComposerPendingUserInputPanel";
import { ComposerPlanFollowUpBanner } from "./chat/ComposerPlanFollowUpBanner";
import { ProviderHealthBanner } from "./chat/ProviderHealthBanner";
import { ThreadErrorBanner } from "./chat/ThreadErrorBanner";
import {
  buildLocalDraftThread,
  buildTemporaryWorktreeBranchName,
  collectUserMessageBlobPreviewUrls,
  LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
  LastInvokedScriptByProjectSchema,
  mergeMessagesForTimeline,
  PullRequestDialogState,
  readFileAsDataUrl,
  revokeBlobPreviewUrl,
  revokeUserMessagePreviewUrls,
  SendPhase,
  getCustomModelOptionsByProvider,
  resolveChatViewShortcutAction,
} from "./ChatView.logic";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { useChromeSpeechRecognition } from "~/hooks/useChromeSpeechRecognition";
import { IS_OPERATORS_APP, type OperatorsSessionSkill } from "~/operators";
import { useOperatorsAuth } from "../operatorsAuth";
import {
  downloadOperatorsSessionFile,
  OPERATORS_SESSION_FILE_MAX_BYTES,
  OPERATORS_SESSION_FILE_MAX_FILES,
  OPERATORS_SESSION_FILE_MAX_TOTAL_BYTES,
  uploadOperatorsSessionFiles,
} from "../lib/operatorsSessionFiles";

const ATTACHMENT_PREVIEW_HANDOFF_TTL_MS = 5000;
const IMAGE_SIZE_LIMIT_LABEL = `${Math.round(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES / (1024 * 1024))}MB`;
const SESSION_FILE_SIZE_LIMIT_LABEL = `${Math.round(OPERATORS_SESSION_FILE_MAX_BYTES / (1024 * 1024))} MiB`;
const SESSION_TOTAL_FILE_SIZE_LIMIT_LABEL = `${Math.round(
  OPERATORS_SESSION_FILE_MAX_TOTAL_BYTES / (1024 * 1024),
)} MiB`;
const EMPTY_ACTIVITIES: OrchestrationThreadActivity[] = [];
const EMPTY_KEYBINDINGS: ResolvedKeybindingsConfig = [];
const EMPTY_PROJECT_ENTRIES: ProjectEntry[] = [];
const EMPTY_AVAILABLE_EDITORS: EditorId[] = [];
const EMPTY_PROVIDER_STATUSES: ServerProviderStatus[] = [];
const EMPTY_CHAT_MESSAGES: ChatMessage[] = [];
const EMPTY_TURNS: OrchestrationLatestTurn[] = [];
const EMPTY_PENDING_USER_INPUT_ANSWERS: Record<string, PendingUserInputDraftAnswer> = {};
const EMPTY_COLLAPSED_TIMELINE_ENTRY_IDS: ReadonlySet<string> = new Set();
const COMPOSER_PATH_QUERY_DEBOUNCE_MS = 120;
const PERSISTED_TURN_STATES_KEY = "t3tools:chat-turn-states:v1";

type PersistedTurnStateKind = Exclude<OrchestrationLatestTurnState, "running">;
interface PersistedTurnState {
  state: PersistedTurnStateKind;
  assistantMessageId: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

type PersistedTurnStatesByThreadId = Record<string, Record<string, PersistedTurnState>>;
const EMPTY_PERSISTED_TURN_STATE_BY_TURN_ID: Record<string, PersistedTurnState> = {};

function isPersistedTurnStateKind(value: unknown): value is PersistedTurnStateKind {
  return value === "completed" || value === "error" || value === "interrupted";
}

function readPersistedTurnStates(): PersistedTurnStatesByThreadId {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(PERSISTED_TURN_STATES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};

    const next: PersistedTurnStatesByThreadId = {};
    for (const [threadId, rawThreadStates] of Object.entries(parsed as Record<string, unknown>)) {
      if (!rawThreadStates || typeof rawThreadStates !== "object") continue;
      const threadStates: Record<string, PersistedTurnState> = {};
      for (const [turnId, rawState] of Object.entries(rawThreadStates as Record<string, unknown>)) {
        if (!rawState || typeof rawState !== "object") continue;
        const record = rawState as Record<string, unknown>;
        if (!isPersistedTurnStateKind(record.state)) continue;
        threadStates[turnId] = {
          state: record.state,
          assistantMessageId:
            typeof record.assistantMessageId === "string" ? record.assistantMessageId : null,
          startedAt: typeof record.startedAt === "string" ? record.startedAt : null,
          completedAt: typeof record.completedAt === "string" ? record.completedAt : null,
        };
      }
      if (Object.keys(threadStates).length > 0) {
        next[threadId] = threadStates;
      }
    }
    return next;
  } catch {
    return {};
  }
}

function writePersistedTurnStates(turnStatesByThreadId: PersistedTurnStatesByThreadId): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PERSISTED_TURN_STATES_KEY, JSON.stringify(turnStatesByThreadId));
  } catch {
    // Ignore quota/storage errors; losing this cache should not block chat usage.
  }
}
const SCRIPT_TERMINAL_COLS = 120;
const SCRIPT_TERMINAL_ROWS = 30;

const extendReplacementRangeForTrailingSpace = (
  text: string,
  rangeEnd: number,
  replacement: string,
): number => {
  if (!replacement.endsWith(" ")) {
    return rangeEnd;
  }
  return text[rangeEnd] === " " ? rangeEnd + 1 : rangeEnd;
};

function formatComposerFileSize(sizeBytes: number): string {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} KiB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(sizeBytes >= 10 * 1024 * 1024 ? 0 : 1)} MiB`;
}

function insertDictatedTranscript(
  value: string,
  expandedCursor: number,
  transcript: string,
): { value: string; expandedCursor: number } | null {
  const normalizedTranscript = transcript.trim();
  if (!normalizedTranscript) return null;

  const cursor = Math.max(0, Math.min(value.length, expandedCursor));
  const before = value.slice(0, cursor);
  const after = value.slice(cursor);
  const leadingSpace = before.length > 0 && !/\s$/.test(before) ? " " : "";
  const trailingWhitespace = after.match(/^\s*/)?.[0] ?? "";
  const trailingSpace = trailingWhitespace.length === 0 ? " " : "";
  const trailingSeparatorLength = trailingSpace.length || trailingWhitespace.length;
  const nextValue = `${before}${leadingSpace}${normalizedTranscript}${trailingSpace}${after}`;

  return {
    value: nextValue,
    expandedCursor:
      before.length + leadingSpace.length + normalizedTranscript.length + trailingSeparatorLength,
  };
}

interface ChatViewProps {
  threadId: ThreadId;
}

export default function ChatView({ threadId }: ChatViewProps) {
  const operatorsMode = IS_OPERATORS_APP;
  const detectComposerTriggerForView = useCallback(
    (text: string, cursor: number) => detectComposerTrigger(text, cursor, operatorsMode),
    [operatorsMode],
  );
  const { user: operatorsUser } = useOperatorsAuth();
  const threads = useStore((store) => store.threads);
  const projects = useStore((store) => store.projects);
  const markThreadVisited = useStore((store) => store.markThreadVisited);
  const setStoreThreadError = useStore((store) => store.setError);
  const setStoreThreadBranch = useStore((store) => store.setThreadBranch);
  const { settings, updateSettings } = useAppSettings();
  const timestampFormat = settings.timestampFormat;
  const navigate = useNavigate();
  const rawSearch = useSearch({
    strict: false,
    select: (params) => parseDiffRouteSearch(params),
  });
  const { resolvedTheme } = useTheme();
  const queryClient = useQueryClient();
  const createWorktreeMutation = useMutation(gitCreateWorktreeMutationOptions({ queryClient }));
  const composerDraft = useComposerThreadDraft(threadId);
  const prompt = composerDraft.prompt;
  const composerImages = composerDraft.images;
  const composerFiles = composerDraft.files;
  const nonPersistedComposerImageIds = composerDraft.nonPersistedImageIds;
  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const setComposerDraftProvider = useComposerDraftStore((store) => store.setProvider);
  const setComposerDraftModel = useComposerDraftStore((store) => store.setModel);
  const setStickyComposerModel = useComposerDraftStore((store) => store.setStickyModel);
  const setComposerDraftInteractionMode = useComposerDraftStore(
    (store) => store.setInteractionMode,
  );
  const setComposerDraftEffort = useComposerDraftStore((store) => store.setEffort);
  const setComposerDraftCodexFastMode = useComposerDraftStore((store) => store.setCodexFastMode);
  const addComposerDraftImage = useComposerDraftStore((store) => store.addImage);
  const addComposerDraftImages = useComposerDraftStore((store) => store.addImages);
  const addComposerDraftFile = useComposerDraftStore((store) => store.addFile);
  const addComposerDraftFiles = useComposerDraftStore((store) => store.addFiles);
  const removeComposerDraftFile = useComposerDraftStore((store) => store.removeFile);
  const removeComposerDraftImage = useComposerDraftStore((store) => store.removeImage);
  const clearComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.clearPersistedAttachments,
  );
  const syncComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.syncPersistedAttachments,
  );
  const clearComposerDraftContent = useComposerDraftStore((store) => store.clearComposerContent);
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);
  const getDraftThreadByProjectId = useComposerDraftStore(
    (store) => store.getDraftThreadByProjectId,
  );
  const getDraftThread = useComposerDraftStore((store) => store.getDraftThread);
  const setProjectDraftThreadId = useComposerDraftStore((store) => store.setProjectDraftThreadId);
  const clearProjectDraftThreadId = useComposerDraftStore(
    (store) => store.clearProjectDraftThreadId,
  );
  const draftThread = useComposerDraftStore(
    (store) => store.draftThreadsByThreadId[threadId] ?? null,
  );
  const promptRef = useRef(prompt);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isDragOverComposer, setIsDragOverComposer] = useState(false);
  const [expandedImage, setExpandedImage] = useState<ExpandedImagePreview | null>(null);
  const [optimisticUserMessages, setOptimisticUserMessages] = useState<ChatMessage[]>([]);
  const optimisticUserMessagesRef = useRef(optimisticUserMessages);
  optimisticUserMessagesRef.current = optimisticUserMessages;
  const [localDraftErrorsByThreadId, setLocalDraftErrorsByThreadId] = useState<
    Record<ThreadId, string | null>
  >({});
  const [sendPhase, setSendPhase] = useState<SendPhase>("idle");
  const [sendStartedAt, setSendStartedAt] = useState<string | null>(null);
  const [isConnecting, _setIsConnecting] = useState(false);
  const [isRevertingCheckpoint, setIsRevertingCheckpoint] = useState(false);
  const [respondingRequestIds, setRespondingRequestIds] = useState<ApprovalRequestId[]>([]);
  const [respondingUserInputRequestIds, setRespondingUserInputRequestIds] = useState<
    ApprovalRequestId[]
  >([]);
  const [pendingUserInputAnswersByRequestId, setPendingUserInputAnswersByRequestId] = useState<
    Record<string, Record<string, PendingUserInputDraftAnswer>>
  >({});
  const [pendingUserInputQuestionIndexByRequestId, setPendingUserInputQuestionIndexByRequestId] =
    useState<Record<string, number>>({});
  const [expandedWorkGroups, setExpandedWorkGroups] = useState<Record<string, boolean>>({});
  const [expandedCompletedTurnKeys, setExpandedCompletedTurnKeys] = useState<
    Record<string, boolean>
  >({});
  const [persistedTurnStatesByThreadId, setPersistedTurnStatesByThreadId] =
    useState(readPersistedTurnStates);
  const [planSidebarOpen, setPlanSidebarOpen] = useState(false);
  const [isComposerFooterCompact, setIsComposerFooterCompact] = useState(false);
  // Tracks whether the user explicitly dismissed the sidebar for the active turn.
  const planSidebarDismissedForTurnRef = useRef<string | null>(null);
  // When set, the thread-change reset effect will open the sidebar instead of closing it.
  // Used by "Implement in a new thread" to carry the sidebar-open intent across navigation.
  const planSidebarOpenOnNextThreadRef = useRef(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [terminalFocusRequestId, setTerminalFocusRequestId] = useState(0);
  const [composerHighlightedItemId, setComposerHighlightedItemId] = useState<string | null>(null);
  const [pullRequestDialogState, setPullRequestDialogState] =
    useState<PullRequestDialogState | null>(null);
  const [attachmentPreviewHandoffByMessageId, setAttachmentPreviewHandoffByMessageId] = useState<
    Record<string, string[]>
  >({});
  const [composerCursor, setComposerCursor] = useState(() =>
    collapseExpandedComposerCursor(prompt, prompt.length),
  );
  const [composerTrigger, setComposerTrigger] = useState<ComposerTrigger | null>(() =>
    detectComposerTriggerForView(prompt, prompt.length),
  );
  const [lastInvokedScriptByProjectId, setLastInvokedScriptByProjectId] = useLocalStorage(
    LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
    {},
    LastInvokedScriptByProjectSchema,
  );
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const [messagesScrollElement, setMessagesScrollElement] = useState<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const lastKnownScrollTopRef = useRef(0);
  const isPointerScrollActiveRef = useRef(false);
  const lastTouchClientYRef = useRef<number | null>(null);
  const pendingUserScrollUpIntentRef = useRef(false);
  const pendingAutoScrollFrameRef = useRef<number | null>(null);
  const pendingInteractionAnchorRef = useRef<{
    element: HTMLElement;
    top: number;
  } | null>(null);
  const pendingInteractionAnchorFrameRef = useRef<number | null>(null);
  const composerEditorRef = useRef<ComposerPromptEditorHandle>(null);
  const speechDictationAnchorRef = useRef<{
    value: string;
    expandedCursor: number;
    threadId: ThreadId;
  } | null>(null);
  const composerFileInputRef = useRef<HTMLInputElement>(null);
  const composerFormRef = useRef<HTMLFormElement>(null);
  const composerFormHeightRef = useRef(0);
  const composerFilesRef = useRef<ComposerFileAttachment[]>([]);
  const composerImagesRef = useRef<ComposerImageAttachment[]>([]);
  const composerSelectLockRef = useRef(false);
  const composerMenuOpenRef = useRef(false);
  const composerMenuItemsRef = useRef<ComposerCommandItem[]>([]);
  const activeComposerMenuItemRef = useRef<ComposerCommandItem | null>(null);
  const attachmentPreviewHandoffByMessageIdRef = useRef<Record<string, string[]>>({});
  const attachmentPreviewHandoffTimeoutByMessageIdRef = useRef<Record<string, number>>({});
  const sendInFlightRef = useRef(false);
  const dragDepthRef = useRef(0);
  const terminalOpenByThreadRef = useRef<Record<string, boolean>>({});
  const setMessagesScrollContainerRef = useCallback((element: HTMLDivElement | null) => {
    messagesScrollRef.current = element;
    setMessagesScrollElement(element);
  }, []);

  const terminalState = useTerminalStateStore((state) =>
    selectThreadTerminalState(state.terminalStateByThreadId, threadId),
  );
  const storeSetTerminalOpen = useTerminalStateStore((s) => s.setTerminalOpen);
  const storeSetTerminalHeight = useTerminalStateStore((s) => s.setTerminalHeight);
  const storeSplitTerminal = useTerminalStateStore((s) => s.splitTerminal);
  const storeNewTerminal = useTerminalStateStore((s) => s.newTerminal);
  const storeSetActiveTerminal = useTerminalStateStore((s) => s.setActiveTerminal);
  const storeCloseTerminal = useTerminalStateStore((s) => s.closeTerminal);

  const setPrompt = useCallback(
    (nextPrompt: string) => {
      setComposerDraftPrompt(threadId, nextPrompt);
    },
    [setComposerDraftPrompt, threadId],
  );
  const clearComposerPromptState = useCallback(
    (draftThreadId?: ThreadId) => {
      promptRef.current = "";
      setPrompt("");
      if (draftThreadId) {
        clearComposerDraftContent(draftThreadId);
      }
      setComposerHighlightedItemId(null);
      setComposerCursor(0);
      setComposerTrigger(null);
    },
    [clearComposerDraftContent, setPrompt],
  );
  const restoreComposerPromptState = useCallback(
    (nextPrompt: string) => {
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      setComposerHighlightedItemId(null);
      const nextCursor = collapseExpandedComposerCursor(nextPrompt, nextPrompt.length);
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTriggerForView(nextPrompt, nextPrompt.length));
    },
    [detectComposerTriggerForView, setPrompt],
  );
  const addComposerImage = useCallback(
    (image: ComposerImageAttachment) => {
      addComposerDraftImage(threadId, image);
    },
    [addComposerDraftImage, threadId],
  );
  const addComposerFile = useCallback(
    (file: ComposerFileAttachment) => {
      addComposerDraftFile(threadId, file);
    },
    [addComposerDraftFile, threadId],
  );
  const addComposerFilesToDraft = useCallback(
    (files: ComposerFileAttachment[]) => {
      addComposerDraftFiles(threadId, files);
    },
    [addComposerDraftFiles, threadId],
  );
  const addComposerImagesToDraft = useCallback(
    (images: ComposerImageAttachment[]) => {
      addComposerDraftImages(threadId, images);
    },
    [addComposerDraftImages, threadId],
  );
  const removeComposerFileFromDraft = useCallback(
    (fileId: string) => {
      removeComposerDraftFile(threadId, fileId);
    },
    [removeComposerDraftFile, threadId],
  );
  const removeComposerImageFromDraft = useCallback(
    (imageId: string) => {
      removeComposerDraftImage(threadId, imageId);
    },
    [removeComposerDraftImage, threadId],
  );

  const serverThread = threads.find((t) => t.id === threadId);
  const fallbackDraftProject = projects.find((project) => project.id === draftThread?.projectId);
  const localDraftError = serverThread ? null : (localDraftErrorsByThreadId[threadId] ?? null);
  const localDraftThread = useMemo(
    () =>
      draftThread
        ? buildLocalDraftThread(
            threadId,
            draftThread,
            fallbackDraftProject?.model ?? DEFAULT_MODEL_BY_PROVIDER.codex,
            localDraftError,
          )
        : undefined,
    [draftThread, fallbackDraftProject?.model, localDraftError, threadId],
  );
  const activeThread = serverThread ?? localDraftThread;
  const runtimeMode =
    composerDraft.runtimeMode ?? activeThread?.runtimeMode ?? DEFAULT_RUNTIME_MODE;
  const interactionMode =
    composerDraft.interactionMode ?? activeThread?.interactionMode ?? DEFAULT_INTERACTION_MODE;
  const isServerThread = serverThread !== undefined;
  const isLocalDraftThread = !isServerThread && localDraftThread !== undefined;
  const canCheckoutPullRequestIntoThread = isLocalDraftThread;
  const diffOpen = rawSearch.diff === "1";
  const showCodeModeSurfaces = !operatorsMode || settings.operatorsSurface === "code";
  const activeThreadId = activeThread?.id ?? null;
  const activeThreadMessages = activeThread?.messages ?? EMPTY_CHAT_MESSAGES;
  const activeThreadTurns = activeThread?.turns ?? EMPTY_TURNS;
  const activeThreadSession = activeThread?.session ?? null;
  const rawActiveLatestTurn = activeThread?.latestTurn ?? null;
  const canUseTerminalTurnFallback =
    activeThreadSession === null ||
    activeThreadSession.orchestrationStatus === "idle" ||
    activeThreadSession.orchestrationStatus === "ready" ||
    activeThreadSession.orchestrationStatus === "stopped";
  const activeLatestTurn = useMemo(() => {
    if (rawActiveLatestTurn) {
      return rawActiveLatestTurn;
    }
    if (!canUseTerminalTurnFallback) {
      return null;
    }
    return (
      activeThreadTurns
        .filter((turn) => turn.state === "completed")
        .toSorted((left, right) =>
          (right.completedAt ?? right.startedAt ?? right.requestedAt).localeCompare(
            left.completedAt ?? left.startedAt ?? left.requestedAt,
          ),
        )[0] ?? null
    );
  }, [activeThreadTurns, canUseTerminalTurnFallback, rawActiveLatestTurn]);
  const latestTurnSettled = isLatestTurnSettled(activeLatestTurn, activeThreadSession);
  const activeProject = projects.find((p) => p.id === activeThread?.projectId);
  const cachedOperatorsSession =
    operatorsMode && isServerThread && activeThread
      ? getCachedOperatorsSession(queryClient, activeThread.id)
      : undefined;
  const { data: activeOperatorsSession } = useQuery(
    operatorsSessionQueryOptions({
      enabled: operatorsMode && isServerThread && activeThread !== undefined,
      initialData: cachedOperatorsSession,
      sessionId: activeThread?.id ?? "",
    }),
  );
  const sessionSkillsQuery = useQuery(
    operatorsSessionSkillsQueryOptions({
      enabled: operatorsMode && isServerThread && activeThread !== undefined,
      sessionId: activeThread?.id ?? "",
    }),
  );
  const skillsLibraryQuery = useQuery({
    ...operatorsSkillsQueryOptions(),
    enabled: operatorsMode && !isServerThread && activeThread !== undefined,
  });
  const sessionSkills = useMemo<OperatorsSessionSkill[]>(() => {
    if (isServerThread) {
      return sessionSkillsQuery.data?.items ?? [];
    }
    return (skillsLibraryQuery.data?.items ?? []).map((skill) => ({
      availableInSession: skill.status === "active",
      description: skill.description,
      name: skill.name,
      status: skill.status,
      version: skill.version,
    }));
  }, [isServerThread, sessionSkillsQuery.data?.items, skillsLibraryQuery.data?.items]);
  useEffect(() => {
    if (!operatorsMode || !isServerThread || !activeThread?.id || !activeLatestTurn?.completedAt) {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: operatorsSkillQueryKeys.session(activeThread.id),
    });
  }, [activeLatestTurn?.completedAt, activeThread?.id, isServerThread, operatorsMode, queryClient]);
  const isArchivedThread =
    operatorsMode && activeThread?.deletedAt !== null && activeThread?.deletedAt !== undefined;
  const optimisticMessageAuthorEmail = operatorsUser?.email ?? null;
  const optimisticMessageAuthorName = operatorsUser?.name ?? null;
  const operatorsSessionFileTransferId = operatorsMode ? (activeThread?.id ?? null) : null;

  const openPullRequestDialog = useCallback(
    (reference?: string) => {
      if (!canCheckoutPullRequestIntoThread) {
        return;
      }
      setPullRequestDialogState({
        initialReference: reference ?? null,
        key: Date.now(),
      });
      setComposerHighlightedItemId(null);
    },
    [canCheckoutPullRequestIntoThread],
  );

  const onWorkspaceFileLinkClick = useCallback(
    (workspacePath: string) => {
      if (!operatorsSessionFileTransferId) {
        toastManager.add({
          type: "error",
          title: "Session download unavailable",
          description: "This thread is not linked to an active Operators session.",
        });
        return;
      }

      void downloadOperatorsSessionFile(operatorsSessionFileTransferId, workspacePath).catch(
        (error) => {
          toastManager.add({
            type: "error",
            title: "Could not download file",
            description:
              error instanceof Error
                ? error.message
                : "An error occurred while downloading the file.",
          });
        },
      );
    },
    [operatorsSessionFileTransferId],
  );

  const buildTurnImageAttachments = useCallback(
    async (images: ComposerImageAttachment[]) =>
      await Promise.all(
        images.map(async (image) => ({
          type: "image" as const,
          name: image.name,
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
          dataUrl: await readFileAsDataUrl(image.file),
        })),
      ),
    [],
  );

  const uploadWorkspaceFileReferences = useCallback(
    async (sessionId: string, files: ComposerFileAttachment[]) => {
      if (files.length === 0) {
        return [];
      }

      const uploadedWorkspaceFiles = await uploadOperatorsSessionFiles(
        sessionId,
        files.map((file) => file.file),
      );
      return uploadedWorkspaceFiles.map((file) => ({
        workspacePath: file.workspacePath,
      }));
    },
    [],
  );

  const closePullRequestDialog = useCallback(() => {
    setPullRequestDialogState(null);
  }, []);

  const openOrReuseProjectDraftThread = useCallback(
    async (input: { branch: string; worktreePath: string | null; envMode: DraftThreadEnvMode }) => {
      if (!activeProject) {
        throw new Error("No active project is available for this pull request.");
      }
      const storedDraftThread = getDraftThreadByProjectId(activeProject.id);
      if (storedDraftThread) {
        setDraftThreadContext(storedDraftThread.threadId, input);
        setProjectDraftThreadId(activeProject.id, storedDraftThread.threadId, input);
        if (storedDraftThread.threadId !== threadId) {
          await navigate({
            to: "/operators/$threadId",
            params: { threadId: storedDraftThread.threadId },
          });
        }
        return;
      }

      const activeDraftThread = getDraftThread(threadId);
      if (!isServerThread && activeDraftThread?.projectId === activeProject.id) {
        setDraftThreadContext(threadId, input);
        setProjectDraftThreadId(activeProject.id, threadId, input);
        return;
      }

      clearProjectDraftThreadId(activeProject.id);
      const nextThreadId = newThreadId();
      setProjectDraftThreadId(activeProject.id, nextThreadId, {
        createdAt: new Date().toISOString(),
        runtimeMode: DEFAULT_RUNTIME_MODE,
        interactionMode: DEFAULT_INTERACTION_MODE,
        ...input,
      });
      await navigate({
        to: "/operators/$threadId",
        params: { threadId: nextThreadId },
      });
    },
    [
      activeProject,
      clearProjectDraftThreadId,
      getDraftThread,
      getDraftThreadByProjectId,
      isServerThread,
      navigate,
      setDraftThreadContext,
      setProjectDraftThreadId,
      threadId,
    ],
  );

  const handlePreparedPullRequestThread = useCallback(
    async (input: { branch: string; worktreePath: string | null }) => {
      await openOrReuseProjectDraftThread({
        branch: input.branch,
        worktreePath: input.worktreePath,
        envMode: input.worktreePath ? "worktree" : "local",
      });
    },
    [openOrReuseProjectDraftThread],
  );

  useEffect(() => {
    if (!activeThread?.id) return;
    if (!latestTurnSettled) return;
    if (!activeLatestTurn?.completedAt) return;
    const turnCompletedAt = Date.parse(activeLatestTurn.completedAt);
    if (Number.isNaN(turnCompletedAt)) return;
    const lastVisitedAt = activeThread.lastVisitedAt ? Date.parse(activeThread.lastVisitedAt) : NaN;
    if (!Number.isNaN(lastVisitedAt) && lastVisitedAt >= turnCompletedAt) return;

    markThreadVisited(activeThread.id);
  }, [
    activeThread?.id,
    activeThread?.lastVisitedAt,
    activeLatestTurn?.completedAt,
    latestTurnSettled,
    markThreadVisited,
  ]);

  const sessionProvider = activeThread?.session?.provider ?? null;
  const selectedProviderByThreadId = composerDraft.provider;
  const hasThreadStarted = Boolean(
    activeThread &&
    (activeThread.latestTurn !== null ||
      activeThread.messages.length > 0 ||
      activeThread.session !== null),
  );
  const lockedProvider: ProviderKind | null = hasThreadStarted
    ? (sessionProvider ?? selectedProviderByThreadId ?? null)
    : null;
  const selectedProvider: ProviderKind = lockedProvider ?? selectedProviderByThreadId ?? "codex";
  const isGrandfatheredLegacyModel =
    isServerThread && isLegacyModelSlug(activeThread?.model, selectedProvider);
  const resolvedBaseThreadModel = resolveModelSlugForProvider(
    selectedProvider,
    activeThread?.model ?? activeProject?.model ?? getDefaultModel(selectedProvider),
  );
  const baseThreadModel =
    isLegacyModelSlug(resolvedBaseThreadModel, selectedProvider) && !isGrandfatheredLegacyModel
      ? getDefaultModel(selectedProvider)
      : resolvedBaseThreadModel;
  const customModelsForSelectedProvider = settings.customCodexModels;
  const selectedModel = useMemo(() => {
    const draftModel = composerDraft.model;
    if (!draftModel) {
      return baseThreadModel;
    }
    const resolvedModel = resolveAppModelSelection(
      selectedProvider,
      customModelsForSelectedProvider,
      draftModel,
    ) as ModelSlug;
    return isLegacyModelSlug(resolvedModel, selectedProvider) &&
      (!isGrandfatheredLegacyModel || resolvedModel !== baseThreadModel)
      ? baseThreadModel
      : resolvedModel;
  }, [
    baseThreadModel,
    composerDraft.model,
    customModelsForSelectedProvider,
    isGrandfatheredLegacyModel,
    selectedProvider,
  ]);
  const reasoningOptions = getReasoningEffortOptions(selectedProvider);
  const supportsReasoningEffort = reasoningOptions.length > 0;
  const selectedCodexModelOptions = composerDraft.modelOptions?.codex;
  const selectedEffort =
    selectedProvider === "codex"
      ? (selectedCodexModelOptions?.reasoningEffort ??
        getRecommendedReasoningEffort(selectedModel, "codex") ??
        getDefaultReasoningEffort("codex"))
      : getDefaultReasoningEffort(selectedProvider);
  const recommendedEffort = getRecommendedReasoningEffort(selectedModel, selectedProvider);
  const selectedCodexFastModeEnabled =
    selectedProvider === "codex" ? selectedCodexModelOptions?.fastMode === true : false;
  const selectedModelOptionsForDispatch = useMemo(() => {
    if (selectedProvider !== "codex") {
      return undefined;
    }
    const codexOptions = {
      ...(supportsReasoningEffort && selectedEffort ? { reasoningEffort: selectedEffort } : {}),
      ...(selectedCodexFastModeEnabled ? { fastMode: true } : {}),
    };
    return Object.keys(codexOptions).length > 0 ? { codex: codexOptions } : undefined;
  }, [selectedCodexFastModeEnabled, selectedEffort, selectedProvider, supportsReasoningEffort]);
  const providerOptionsForDispatch = useMemo(() => {
    if (!settings.codexBinaryPath && !settings.codexHomePath) {
      return undefined;
    }
    return {
      codex: {
        ...(settings.codexBinaryPath ? { binaryPath: settings.codexBinaryPath } : {}),
        ...(settings.codexHomePath ? { homePath: settings.codexHomePath } : {}),
      },
    };
  }, [settings.codexBinaryPath, settings.codexHomePath]);
  const selectedModelForPicker = selectedModel;
  const modelOptionsByProvider = useMemo(
    () =>
      getCustomModelOptionsByProvider(
        settings,
        isGrandfatheredLegacyModel ? activeThread?.model : null,
      ),
    [activeThread?.model, isGrandfatheredLegacyModel, settings],
  );
  const selectedModelForPickerWithCustomFallback = useMemo(() => {
    const currentOptions = modelOptionsByProvider[selectedProvider];
    return currentOptions.some((option) => option.slug === selectedModelForPicker)
      ? selectedModelForPicker
      : (normalizeModelSlug(selectedModelForPicker, selectedProvider) ?? selectedModelForPicker);
  }, [modelOptionsByProvider, selectedModelForPicker, selectedProvider]);
  const searchableModelOptions = useMemo(
    () =>
      AVAILABLE_PROVIDER_OPTIONS.filter(
        (option) => lockedProvider === null || option.value === lockedProvider,
      ).flatMap((option) =>
        modelOptionsByProvider[option.value].map(({ slug, name }) => ({
          provider: option.value,
          providerLabel: option.label,
          slug,
          name,
          searchSlug: slug.toLowerCase(),
          searchName: name.toLowerCase(),
          searchProvider: option.label.toLowerCase(),
        })),
      ),
    [lockedProvider, modelOptionsByProvider],
  );
  const phase = isArchivedThread ? "disconnected" : derivePhase(activeThreadSession);
  const isSendBusy = sendPhase !== "idle";
  const isPreparingWorktree = sendPhase === "preparing-worktree";
  const isWorking = phase === "running" || isSendBusy || isConnecting || isRevertingCheckpoint;
  const isTurnStartingOrRunning =
    activeThreadSession?.orchestrationStatus === "starting" ||
    activeThreadSession?.orchestrationStatus === "running";
  const nowIso = new Date(nowTick).toISOString();
  const activeWorkStartedAt = deriveActiveWorkStartedAt(
    activeLatestTurn,
    activeThreadSession,
    sendStartedAt,
  );
  const threadActivities = activeThread?.activities ?? EMPTY_ACTIVITIES;
  const workLogEntries = useMemo(
    () => deriveWorkLogEntries(threadActivities, undefined),
    [threadActivities],
  );
  const pendingApprovals = useMemo(
    () => derivePendingApprovals(threadActivities),
    [threadActivities],
  );
  const pendingUserInputs = useMemo(
    () => derivePendingUserInputs(threadActivities),
    [threadActivities],
  );
  const activePendingUserInput = pendingUserInputs[0] ?? null;
  const activePendingDraftAnswers = useMemo(
    () =>
      activePendingUserInput
        ? (pendingUserInputAnswersByRequestId[activePendingUserInput.requestId] ??
          EMPTY_PENDING_USER_INPUT_ANSWERS)
        : EMPTY_PENDING_USER_INPUT_ANSWERS,
    [activePendingUserInput, pendingUserInputAnswersByRequestId],
  );
  const activePendingQuestionIndex = activePendingUserInput
    ? (pendingUserInputQuestionIndexByRequestId[activePendingUserInput.requestId] ?? 0)
    : 0;
  const activePendingProgress = useMemo(
    () =>
      activePendingUserInput
        ? derivePendingUserInputProgress(
            activePendingUserInput.questions,
            activePendingDraftAnswers,
            activePendingQuestionIndex,
          )
        : null,
    [activePendingDraftAnswers, activePendingQuestionIndex, activePendingUserInput],
  );
  const activePendingResolvedAnswers = useMemo(
    () =>
      activePendingUserInput
        ? buildPendingUserInputAnswers(activePendingUserInput.questions, activePendingDraftAnswers)
        : null,
    [activePendingDraftAnswers, activePendingUserInput],
  );
  const activePendingIsResponding = activePendingUserInput
    ? respondingUserInputRequestIds.includes(activePendingUserInput.requestId)
    : false;
  const activeProposedPlan = useMemo(() => {
    if (!latestTurnSettled) {
      return null;
    }
    return findLatestProposedPlan(
      activeThread?.proposedPlans ?? [],
      activeLatestTurn?.turnId ?? null,
    );
  }, [activeLatestTurn?.turnId, activeThread?.proposedPlans, latestTurnSettled]);
  const activePlan = useMemo(
    () => deriveActivePlanState(threadActivities, activeLatestTurn?.turnId ?? undefined),
    [activeLatestTurn?.turnId, threadActivities],
  );
  const showPlanFollowUpPrompt =
    pendingUserInputs.length === 0 &&
    interactionMode === "plan" &&
    latestTurnSettled &&
    activeProposedPlan !== null;
  const activePendingApproval = pendingApprovals[0] ?? null;
  const isComposerApprovalState = activePendingApproval !== null;
  const hasComposerHeader =
    isComposerApprovalState ||
    pendingUserInputs.length > 0 ||
    (showPlanFollowUpPrompt && activeProposedPlan !== null);
  const composerFooterHasWideActions = showPlanFollowUpPrompt || activePendingProgress !== null;
  const lastSyncedPendingInputRef = useRef<{
    requestId: string | null;
    questionId: string | null;
  } | null>(null);
  useEffect(() => {
    const nextCustomAnswer = activePendingProgress?.customAnswer;
    if (typeof nextCustomAnswer !== "string") {
      lastSyncedPendingInputRef.current = null;
      return;
    }
    const nextRequestId = activePendingUserInput?.requestId ?? null;
    const nextQuestionId = activePendingProgress?.activeQuestion?.id ?? null;
    const questionChanged =
      lastSyncedPendingInputRef.current?.requestId !== nextRequestId ||
      lastSyncedPendingInputRef.current?.questionId !== nextQuestionId;
    const textChangedExternally = promptRef.current !== nextCustomAnswer;

    lastSyncedPendingInputRef.current = {
      requestId: nextRequestId,
      questionId: nextQuestionId,
    };

    if (!questionChanged && !textChangedExternally) {
      return;
    }

    promptRef.current = nextCustomAnswer;
    const nextCursor = collapseExpandedComposerCursor(nextCustomAnswer, nextCustomAnswer.length);
    setComposerCursor(nextCursor);
    setComposerTrigger(
      detectComposerTriggerForView(
        nextCustomAnswer,
        expandCollapsedComposerCursor(nextCustomAnswer, nextCursor),
      ),
    );
    setComposerHighlightedItemId(null);
  }, [
    activePendingProgress?.customAnswer,
    activePendingUserInput?.requestId,
    activePendingProgress?.activeQuestion?.id,
    detectComposerTriggerForView,
  ]);
  useEffect(() => {
    attachmentPreviewHandoffByMessageIdRef.current = attachmentPreviewHandoffByMessageId;
  }, [attachmentPreviewHandoffByMessageId]);
  const clearAttachmentPreviewHandoffs = useCallback(() => {
    for (const timeoutId of Object.values(attachmentPreviewHandoffTimeoutByMessageIdRef.current)) {
      window.clearTimeout(timeoutId);
    }
    attachmentPreviewHandoffTimeoutByMessageIdRef.current = {};
    for (const previewUrls of Object.values(attachmentPreviewHandoffByMessageIdRef.current)) {
      for (const previewUrl of previewUrls) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    attachmentPreviewHandoffByMessageIdRef.current = {};
    setAttachmentPreviewHandoffByMessageId({});
  }, []);
  useEffect(() => {
    return () => {
      clearAttachmentPreviewHandoffs();
      for (const message of optimisticUserMessagesRef.current) {
        revokeUserMessagePreviewUrls(message);
      }
    };
  }, [clearAttachmentPreviewHandoffs]);
  const handoffAttachmentPreviews = useCallback((messageId: MessageId, previewUrls: string[]) => {
    if (previewUrls.length === 0) return;

    const previousPreviewUrls = attachmentPreviewHandoffByMessageIdRef.current[messageId] ?? [];
    for (const previewUrl of previousPreviewUrls) {
      if (!previewUrls.includes(previewUrl)) {
        revokeBlobPreviewUrl(previewUrl);
      }
    }
    setAttachmentPreviewHandoffByMessageId((existing) => {
      const next = {
        ...existing,
        [messageId]: previewUrls,
      };
      attachmentPreviewHandoffByMessageIdRef.current = next;
      return next;
    });

    const existingTimeout = attachmentPreviewHandoffTimeoutByMessageIdRef.current[messageId];
    if (typeof existingTimeout === "number") {
      window.clearTimeout(existingTimeout);
    }
    attachmentPreviewHandoffTimeoutByMessageIdRef.current[messageId] = window.setTimeout(() => {
      const currentPreviewUrls = attachmentPreviewHandoffByMessageIdRef.current[messageId];
      if (currentPreviewUrls) {
        for (const previewUrl of currentPreviewUrls) {
          revokeBlobPreviewUrl(previewUrl);
        }
      }
      setAttachmentPreviewHandoffByMessageId((existing) => {
        if (!(messageId in existing)) return existing;
        const next = { ...existing };
        delete next[messageId];
        attachmentPreviewHandoffByMessageIdRef.current = next;
        return next;
      });
      delete attachmentPreviewHandoffTimeoutByMessageIdRef.current[messageId];
    }, ATTACHMENT_PREVIEW_HANDOFF_TTL_MS);
  }, []);
  const serverMessages = activeThreadMessages;
  const timelineMessages = useMemo(() => {
    const messages = serverMessages;
    const serverMessagesWithPreviewHandoff =
      Object.keys(attachmentPreviewHandoffByMessageId).length === 0
        ? messages
        : // Spread only fires for the few messages that actually changed;
          // unchanged ones early-return their original reference.
          // In-place mutation would break React's immutable state contract.
          messages.map((message) => {
            if (
              message.role !== "user" ||
              !message.attachments ||
              message.attachments.length === 0
            ) {
              return message;
            }
            const handoffPreviewUrls = attachmentPreviewHandoffByMessageId[message.id];
            if (!handoffPreviewUrls || handoffPreviewUrls.length === 0) {
              return message;
            }

            let changed = false;
            let imageIndex = 0;
            const attachments = message.attachments.map((attachment) => {
              if (attachment.type !== "image") {
                return attachment;
              }
              const handoffPreviewUrl = handoffPreviewUrls[imageIndex];
              imageIndex += 1;
              if (!handoffPreviewUrl || attachment.previewUrl === handoffPreviewUrl) {
                return attachment;
              }
              changed = true;
              return {
                ...attachment,
                previewUrl: handoffPreviewUrl,
              };
            });

            return changed ? { ...message, attachments } : message;
          });

    if (optimisticUserMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    const serverIds = new Set(serverMessagesWithPreviewHandoff.map((message) => message.id));
    const pendingMessages = optimisticUserMessages.filter((message) => !serverIds.has(message.id));
    if (pendingMessages.length === 0) {
      return serverMessagesWithPreviewHandoff;
    }
    return mergeMessagesForTimeline(serverMessagesWithPreviewHandoff, pendingMessages);
  }, [serverMessages, attachmentPreviewHandoffByMessageId, optimisticUserMessages]);
  const activePromptAnchor = useMemo(
    () => deriveTimelineActivePromptAnchor(timelineMessages, activeLatestTurn),
    [activeLatestTurn, timelineMessages],
  );
  const timelineEntries = useMemo(
    () =>
      deriveTimelineEntries(timelineMessages, activeThread?.proposedPlans ?? [], workLogEntries, {
        activePromptAnchor,
      }),
    [activePromptAnchor, activeThread?.proposedPlans, timelineMessages, workLogEntries],
  );
  const { turnDiffSummaries, inferredCheckpointTurnCountByTurnId } =
    useTurnDiffSummaries(activeThread);
  useEffect(() => {
    if (!activeThreadId) {
      return;
    }
    setPersistedTurnStatesByThreadId((existing) => {
      const existingThreadStates = existing[activeThreadId] ?? {};
      let nextThreadStates = existingThreadStates;
      const upsertTurnState = (
        turnId: TurnId,
        state: PersistedTurnStateKind,
        values: {
          assistantMessageId?: MessageId | null | undefined;
          startedAt?: string | null | undefined;
          completedAt?: string | null | undefined;
        },
      ) => {
        const key = turnId as string;
        const previous = nextThreadStates[key];
        const nextState: PersistedTurnState = {
          state,
          assistantMessageId:
            values.assistantMessageId === undefined
              ? (previous?.assistantMessageId ?? null)
              : values.assistantMessageId,
          startedAt:
            values.startedAt === undefined ? (previous?.startedAt ?? null) : values.startedAt,
          completedAt:
            values.completedAt === undefined ? (previous?.completedAt ?? null) : values.completedAt,
        };
        if (
          previous?.state === nextState.state &&
          previous.assistantMessageId === nextState.assistantMessageId &&
          previous.startedAt === nextState.startedAt &&
          previous.completedAt === nextState.completedAt
        ) {
          return;
        }
        if (nextThreadStates === existingThreadStates) {
          nextThreadStates = { ...existingThreadStates };
        }
        nextThreadStates[key] = nextState;
      };

      for (const summary of turnDiffSummaries) {
        upsertTurnState(summary.turnId, "completed", {
          assistantMessageId: summary.assistantMessageId ?? null,
          completedAt: summary.completedAt,
        });
      }

      for (const turn of activeThread?.turns ?? []) {
        if (turn.state === "running") {
          continue;
        }
        upsertTurnState(turn.turnId, turn.state, {
          assistantMessageId: turn.assistantMessageId,
          startedAt: turn.startedAt,
          completedAt: turn.completedAt,
        });
      }

      if (rawActiveLatestTurn && rawActiveLatestTurn.state !== "running") {
        upsertTurnState(rawActiveLatestTurn.turnId, rawActiveLatestTurn.state, {
          assistantMessageId: rawActiveLatestTurn.assistantMessageId,
          startedAt: rawActiveLatestTurn.startedAt,
          completedAt: rawActiveLatestTurn.completedAt,
        });
      }

      if (nextThreadStates === existingThreadStates) {
        return existing;
      }
      return {
        ...existing,
        [activeThreadId]: nextThreadStates,
      };
    });
  }, [activeThread?.turns, activeThreadId, rawActiveLatestTurn, turnDiffSummaries]);
  useEffect(() => {
    writePersistedTurnStates(persistedTurnStatesByThreadId);
  }, [persistedTurnStatesByThreadId]);
  const persistedTurnStateByTurnId = activeThreadId
    ? (persistedTurnStatesByThreadId[activeThreadId] ?? EMPTY_PERSISTED_TURN_STATE_BY_TURN_ID)
    : EMPTY_PERSISTED_TURN_STATE_BY_TURN_ID;
  const turnDiffSummaryByAssistantMessageId = useMemo(() => {
    const byMessageId = new Map<MessageId, TurnDiffSummary>();
    for (const summary of turnDiffSummaries) {
      if (!summary.assistantMessageId) continue;
      byMessageId.set(summary.assistantMessageId, summary);
    }
    return byMessageId;
  }, [turnDiffSummaries]);
  const excludedCompletedTurnIds = useMemo(() => {
    const next = new Set<TurnId>();
    for (const [turnId, turnState] of Object.entries(persistedTurnStateByTurnId)) {
      if (turnState.state !== "completed") {
        next.add(turnId as TurnId);
      }
    }
    for (const turn of activeThreadTurns) {
      if (turn.state !== "completed") {
        next.add(turn.turnId);
      }
    }
    if (activeLatestTurn?.turnId && activeLatestTurn.state !== "completed") {
      next.add(activeLatestTurn.turnId);
    }
    if (activeThreadSession?.activeTurnId && isTurnStartingOrRunning) {
      next.add(activeThreadSession.activeTurnId);
    }
    return next;
  }, [
    activeLatestTurn?.state,
    activeLatestTurn?.turnId,
    activeThreadTurns,
    activeThreadSession?.activeTurnId,
    isTurnStartingOrRunning,
    persistedTurnStateByTurnId,
  ]);
  const completedTurnIds = useMemo(() => {
    const next = new Set<TurnId>();
    for (const [turnId, turnState] of Object.entries(persistedTurnStateByTurnId)) {
      if (turnState.state === "completed") {
        next.add(turnId as TurnId);
      }
    }
    for (const turn of activeThreadTurns) {
      if (turn.state === "completed") {
        next.add(turn.turnId);
      }
    }
    for (const summary of turnDiffSummaries) {
      next.add(summary.turnId);
    }
    if (latestTurnSettled && activeLatestTurn?.state === "completed") {
      next.add(activeLatestTurn.turnId);
    }
    for (const turnId of excludedCompletedTurnIds) {
      next.delete(turnId);
    }
    return next;
  }, [
    activeLatestTurn?.state,
    activeLatestTurn?.turnId,
    activeThreadTurns,
    excludedCompletedTurnIds,
    latestTurnSettled,
    persistedTurnStateByTurnId,
    turnDiffSummaries,
  ]);
  const finalAssistantMessageIdByTurnId = useMemo(() => {
    const next = new Map<TurnId, MessageId>();
    for (const [turnId, turnState] of Object.entries(persistedTurnStateByTurnId)) {
      if (turnState.state === "completed" && turnState.assistantMessageId) {
        next.set(turnId as TurnId, turnState.assistantMessageId as MessageId);
      }
    }
    for (const turn of activeThreadTurns) {
      if (turn.state === "completed" && turn.assistantMessageId) {
        next.set(turn.turnId, turn.assistantMessageId);
      }
    }
    for (const summary of turnDiffSummaries) {
      if (summary.assistantMessageId) {
        next.set(summary.turnId, summary.assistantMessageId);
      }
    }
    if (
      latestTurnSettled &&
      activeLatestTurn?.state === "completed" &&
      activeLatestTurn.assistantMessageId
    ) {
      next.set(activeLatestTurn.turnId, activeLatestTurn.assistantMessageId);
    }
    return next;
  }, [
    activeLatestTurn?.assistantMessageId,
    activeLatestTurn?.state,
    activeLatestTurn?.turnId,
    activeThreadTurns,
    latestTurnSettled,
    persistedTurnStateByTurnId,
    turnDiffSummaries,
  ]);
  const completedTurnCollapseStates = useMemo(
    () =>
      deriveCompletedTurnCollapseStates(timelineEntries, activeLatestTurn, latestTurnSettled, {
        completedTurnIds,
        excludedTurnIds: excludedCompletedTurnIds,
        finalAssistantMessageIdByTurnId,
      }),
    [
      activeLatestTurn,
      completedTurnIds,
      excludedCompletedTurnIds,
      finalAssistantMessageIdByTurnId,
      latestTurnSettled,
      timelineEntries,
    ],
  );
  const completedTurnCollapseKeyByTurnId = useMemo(() => {
    const next = new Map<TurnId, string>();
    if (!activeThread) {
      return next;
    }
    for (const collapseState of completedTurnCollapseStates) {
      next.set(collapseState.turnId, `${activeThread.id}:${collapseState.turnId}`);
    }
    return next;
  }, [activeThread, completedTurnCollapseStates]);
  const completedTurnSummaryByTurnId = useMemo(() => {
    const next = new Map<TurnId, string>();
    for (const collapseState of completedTurnCollapseStates) {
      const hasToolActivity = hasToolActivityForTurn(threadActivities, collapseState.turnId);
      if (!hasToolActivity && collapseState.collapsedEntryIds.size === 0) {
        continue;
      }
      if (!collapseState.startedAt || !collapseState.completedAt) {
        continue;
      }
      const elapsed = formatElapsed(collapseState.startedAt, collapseState.completedAt);
      next.set(collapseState.turnId, elapsed ? `Worked for ${elapsed}` : "Worked");
    }
    return next;
  }, [completedTurnCollapseStates, threadActivities]);
  const collapsibleCompletedTurnEntryIdsByTurnId = useMemo(() => {
    const next = new Map<TurnId, ReadonlySet<string>>();
    for (const collapseState of completedTurnCollapseStates) {
      if (!completedTurnSummaryByTurnId.has(collapseState.turnId)) {
        continue;
      }

      const entryIds = new Set<string>();
      for (const timelineEntry of timelineEntries) {
        if (!collapseState.collapsedEntryIds.has(timelineEntry.id)) {
          continue;
        }
        if (
          timelineEntry.kind === "message" &&
          turnDiffSummaryByAssistantMessageId.has(timelineEntry.message.id)
        ) {
          continue;
        }
        entryIds.add(timelineEntry.id);
      }
      next.set(collapseState.turnId, entryIds);
    }
    return next;
  }, [
    completedTurnCollapseStates,
    completedTurnSummaryByTurnId,
    timelineEntries,
    turnDiffSummaryByAssistantMessageId,
  ]);
  const collapsedCompletedTurnEntryIds = useMemo(() => {
    const next = new Set<string>();
    for (const [turnId, entryIds] of collapsibleCompletedTurnEntryIdsByTurnId) {
      const collapseKey = completedTurnCollapseKeyByTurnId.get(turnId);
      if (!collapseKey || expandedCompletedTurnKeys[collapseKey] || entryIds.size === 0) {
        continue;
      }
      for (const entryId of entryIds) {
        next.add(entryId);
      }
    }
    return next.size > 0 ? next : EMPTY_COLLAPSED_TIMELINE_ENTRY_IDS;
  }, [
    collapsibleCompletedTurnEntryIdsByTurnId,
    completedTurnCollapseKeyByTurnId,
    expandedCompletedTurnKeys,
  ]);
  const visibleTimelineEntries = useMemo(() => {
    if (collapsedCompletedTurnEntryIds.size === 0) {
      return timelineEntries;
    }
    return timelineEntries.filter((entry) => !collapsedCompletedTurnEntryIds.has(entry.id));
  }, [collapsedCompletedTurnEntryIds, timelineEntries]);
  const revertTurnCountByUserMessageId = useMemo(() => {
    const byUserMessageId = new Map<MessageId, number>();
    for (let index = 0; index < timelineEntries.length; index += 1) {
      const entry = timelineEntries[index];
      if (!entry || entry.kind !== "message" || entry.message.role !== "user") {
        continue;
      }

      for (let nextIndex = index + 1; nextIndex < timelineEntries.length; nextIndex += 1) {
        const nextEntry = timelineEntries[nextIndex];
        if (!nextEntry || nextEntry.kind !== "message") {
          continue;
        }
        if (nextEntry.message.role === "user") {
          break;
        }
        const summary = turnDiffSummaryByAssistantMessageId.get(nextEntry.message.id);
        if (!summary) {
          continue;
        }
        const turnCount =
          summary.checkpointTurnCount ?? inferredCheckpointTurnCountByTurnId[summary.turnId];
        if (typeof turnCount !== "number") {
          break;
        }
        byUserMessageId.set(entry.message.id, Math.max(0, turnCount - 1));
        break;
      }
    }

    return byUserMessageId;
  }, [inferredCheckpointTurnCountByTurnId, timelineEntries, turnDiffSummaryByAssistantMessageId]);
  const activeInProgressTurnId = useMemo(() => {
    if (activeThreadSession?.orchestrationStatus === "starting") {
      return activeThreadSession.activeTurnId ?? null;
    }
    if (activeThreadSession?.orchestrationStatus === "running") {
      if (activeThreadSession.activeTurnId) {
        return activeThreadSession.activeTurnId;
      }
      return activeLatestTurn?.state === "running" ? activeLatestTurn.turnId : null;
    }
    if (activeLatestTurn?.state === "running" && !latestTurnSettled) {
      return activeLatestTurn.turnId;
    }
    return null;
  }, [
    activeLatestTurn?.state,
    activeLatestTurn?.turnId,
    activeThreadSession?.activeTurnId,
    activeThreadSession?.orchestrationStatus,
    latestTurnSettled,
  ]);
  const activeTurnTimingBeforeEntryId = useMemo(() => {
    if (!isWorking || !activeInProgressTurnId) {
      return null;
    }
    return findFirstTimelineEntryIdForTurnContent(visibleTimelineEntries, activeInProgressTurnId);
  }, [activeInProgressTurnId, isWorking, visibleTimelineEntries]);
  const completedTurnTimingDividers = useMemo(
    () =>
      completedTurnCollapseStates.flatMap((collapseState) => {
        const collapseKey = completedTurnCollapseKeyByTurnId.get(collapseState.turnId);
        const summary = completedTurnSummaryByTurnId.get(collapseState.turnId);
        if (!collapseKey || !summary) {
          return [];
        }
        const collapsibleEntryIds =
          collapsibleCompletedTurnEntryIdsByTurnId.get(collapseState.turnId) ??
          EMPTY_COLLAPSED_TIMELINE_ENTRY_IDS;
        return [
          {
            id: collapseKey,
            state: "worked" as const,
            beforeEntryId:
              findFirstTimelineEntryIdForTurnContent(
                visibleTimelineEntries,
                collapseState.turnId,
              ) ?? collapseState.finalEntryId,
            startedAt: collapseState.startedAt,
            completedAt: collapseState.completedAt,
            summary,
            expanded: expandedCompletedTurnKeys[collapseKey] ?? false,
            collapsibleEntryCount: collapsibleEntryIds.size,
          },
        ];
      }),
    [
      collapsibleCompletedTurnEntryIdsByTurnId,
      completedTurnCollapseKeyByTurnId,
      completedTurnCollapseStates,
      completedTurnSummaryByTurnId,
      expandedCompletedTurnKeys,
      visibleTimelineEntries,
    ],
  );
  const workingTurnTimingDivider = useMemo(() => {
    if (!isWorking) return null;
    return {
      id: `working:${activeThread?.id ?? threadId}:${
        activeInProgressTurnId ?? sendStartedAt ?? "pending"
      }`,
      state: "working" as const,
      beforeEntryId: activeTurnTimingBeforeEntryId,
      startedAt: activeWorkStartedAt,
      completedAt: null,
      summary: null,
      expanded: true,
      collapsibleEntryCount: 0,
    };
  }, [
    activeInProgressTurnId,
    activeThread?.id,
    activeTurnTimingBeforeEntryId,
    activeWorkStartedAt,
    isWorking,
    sendStartedAt,
    threadId,
  ]);
  const turnTimingDividers = useMemo(
    () =>
      workingTurnTimingDivider
        ? [...completedTurnTimingDividers, workingTurnTimingDivider]
        : completedTurnTimingDividers,
    [completedTurnTimingDividers, workingTurnTimingDivider],
  );
  const activeTurnInProgress =
    isSendBusy ||
    isConnecting ||
    isTurnStartingOrRunning ||
    (phase === "running" && !latestTurnSettled) ||
    Boolean(activeLatestTurn?.state === "running" && !latestTurnSettled);
  const hasVisibleStreamingAssistantForActiveTurn = useMemo(() => {
    if (!activeInProgressTurnId) {
      return false;
    }
    return visibleTimelineEntries.some(
      (entry) =>
        entry.kind === "message" &&
        entry.message.role === "assistant" &&
        entry.message.turnId === activeInProgressTurnId &&
        entry.message.streaming,
    );
  }, [activeInProgressTurnId, visibleTimelineEntries]);
  const showThinkingIndicator =
    activeTurnInProgress &&
    !hasVisibleStreamingAssistantForActiveTurn &&
    !activePendingApproval &&
    !activePendingUserInput;
  const gitCwd = operatorsMode ? null : (activeThread?.worktreePath ?? activeProject?.cwd ?? null);
  const composerTriggerKind = composerTrigger?.kind ?? null;
  const pathTriggerQuery = composerTrigger?.kind === "path" ? composerTrigger.query : "";
  const isPathTrigger = composerTriggerKind === "path";
  const [debouncedPathQuery, composerPathQueryDebouncer] = useDebouncedValue(
    pathTriggerQuery,
    { wait: COMPOSER_PATH_QUERY_DEBOUNCE_MS },
    (debouncerState) => ({ isPending: debouncerState.isPending }),
  );
  const effectivePathQuery = pathTriggerQuery.length > 0 ? debouncedPathQuery : "";
  const branchesQuery = useQuery({
    ...gitBranchesQueryOptions(gitCwd),
    enabled: !operatorsMode && gitCwd !== null,
  });
  const serverConfigQuery = useQuery(serverConfigQueryOptions());
  const workspaceEntriesQuery = useQuery(
    projectSearchEntriesQueryOptions({
      cwd: gitCwd,
      query: effectivePathQuery,
      enabled: isPathTrigger && !operatorsMode,
      limit: 80,
    }),
  );
  const workspaceEntries = workspaceEntriesQuery.data?.entries ?? EMPTY_PROJECT_ENTRIES;
  const composerMenuItems = useMemo<ComposerCommandItem[]>(() => {
    if (!composerTrigger) return [];
    if (composerTrigger.kind === "path") {
      return workspaceEntries.map((entry) => ({
        id: `path:${entry.kind}:${entry.path}`,
        type: "path",
        path: entry.path,
        pathKind: entry.kind,
        label: basenameOfPath(entry.path),
        description: entry.parentPath ?? "",
      }));
    }

    if (composerTrigger.kind === "slash-command") {
      const slashCommandItems = [
        {
          id: "slash:model",
          type: "slash-command",
          command: "model",
          label: "/model",
          description: "Switch response model for this thread",
        },
        {
          id: "slash:plan",
          type: "slash-command",
          command: "plan",
          label: "/plan",
          description: "Switch this thread into plan mode",
        },
        {
          id: "slash:default",
          type: "slash-command",
          command: "default",
          label: "/default",
          description: "Switch this thread back to normal chat mode",
        },
      ] satisfies ReadonlyArray<Extract<ComposerCommandItem, { type: "slash-command" }>>;
      const skillItems =
        operatorsMode && selectedProvider === "codex"
          ? sessionSkills.map((skill) => ({
              id: `skill:${skill.name}`,
              type: "skill" as const,
              name: skill.name,
              status: skill.status,
              availableInSession: skill.availableInSession,
              label: `/${skill.name}`,
              description: skill.description,
            }))
          : [];
      const query = composerTrigger.query.trim().toLowerCase();
      if (!query) {
        return [...slashCommandItems, ...skillItems];
      }
      return [
        ...slashCommandItems.filter(
          (item) => item.command.includes(query) || item.label.slice(1).includes(query),
        ),
        ...skillItems.filter(
          (item) => item.name.includes(query) || item.description.toLowerCase().includes(query),
        ),
      ];
    }

    if (composerTrigger.kind === "skill") {
      const query = composerTrigger.query.trim().toLowerCase();
      return operatorsMode && selectedProvider === "codex"
        ? sessionSkills
            .filter(
              (skill) =>
                !query ||
                skill.name.includes(query) ||
                skill.description.toLowerCase().includes(query),
            )
            .map((skill) => ({
              id: `skill:${skill.name}`,
              type: "skill" as const,
              name: skill.name,
              status: skill.status,
              availableInSession: skill.availableInSession,
              label: `$${skill.name}`,
              description: skill.description,
            }))
        : [];
    }

    return searchableModelOptions
      .filter(({ searchSlug, searchName, searchProvider }) => {
        const query = composerTrigger.query.trim().toLowerCase();
        if (!query) return true;
        return (
          searchSlug.includes(query) || searchName.includes(query) || searchProvider.includes(query)
        );
      })
      .map(({ provider, providerLabel, slug, name }) => ({
        id: `model:${provider}:${slug}`,
        type: "model",
        provider,
        model: slug,
        label: name,
        description: `${providerLabel} · ${slug}`,
      }));
  }, [
    composerTrigger,
    operatorsMode,
    searchableModelOptions,
    selectedProvider,
    sessionSkills,
    workspaceEntries,
  ]);
  const composerMenuOpen = Boolean(composerTrigger);
  const selectableComposerMenuItems = useMemo(
    () => composerMenuItems.filter((item) => item.type !== "skill" || item.availableInSession),
    [composerMenuItems],
  );
  const activeComposerMenuItem = useMemo(
    () =>
      selectableComposerMenuItems.find((item) => item.id === composerHighlightedItemId) ??
      selectableComposerMenuItems[0] ??
      null,
    [composerHighlightedItemId, selectableComposerMenuItems],
  );
  composerMenuOpenRef.current = composerMenuOpen;
  composerMenuItemsRef.current = composerMenuItems;
  activeComposerMenuItemRef.current = activeComposerMenuItem;
  const nonPersistedComposerImageIdSet = useMemo(
    () => new Set(nonPersistedComposerImageIds),
    [nonPersistedComposerImageIds],
  );
  const keybindings = serverConfigQuery.data?.keybindings ?? EMPTY_KEYBINDINGS;
  const availableEditors = serverConfigQuery.data?.availableEditors ?? EMPTY_AVAILABLE_EDITORS;
  const providerStatuses = serverConfigQuery.data?.providers ?? EMPTY_PROVIDER_STATUSES;
  const activeProvider = activeThread?.session?.provider ?? "codex";
  const activeProviderStatus = useMemo(
    () => providerStatuses.find((status) => status.provider === activeProvider) ?? null,
    [activeProvider, providerStatuses],
  );
  const activeProjectCwd = activeProject?.cwd ?? null;
  const activeThreadWorktreePath = activeThread?.worktreePath ?? null;
  const threadTerminalRuntimeEnv = useMemo(() => {
    if (!activeProjectCwd) return {};
    return projectScriptRuntimeEnv({
      project: {
        cwd: activeProjectCwd,
      },
      worktreePath: activeThreadWorktreePath,
    });
  }, [activeProjectCwd, activeThreadWorktreePath]);
  // Default true while loading to avoid toolbar flicker.
  const isGitRepo = operatorsMode ? false : (branchesQuery.data?.isRepo ?? true);
  const splitTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.split"),
    [keybindings],
  );
  const newTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.new"),
    [keybindings],
  );
  const closeTerminalShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "terminal.close"),
    [keybindings],
  );
  const diffPanelShortcutLabel = useMemo(
    () => shortcutLabelForCommand(keybindings, "diff.toggle"),
    [keybindings],
  );
  const onToggleDiff = useCallback(() => {
    void navigate({
      to: "/operators/$threadId",
      params: { threadId },
      replace: true,
      search: (previous) => {
        const rest = stripDiffSearchParams(previous);
        return diffOpen ? { ...rest, diff: undefined } : { ...rest, diff: "1" };
      },
    });
  }, [diffOpen, navigate, threadId]);
  useEffect(() => {
    if (showCodeModeSurfaces || !diffOpen) return;
    void navigate({
      to: "/operators/$threadId",
      params: { threadId },
      replace: true,
      search: (previous) => ({
        ...stripDiffSearchParams(previous),
        diff: undefined,
      }),
    });
  }, [diffOpen, navigate, showCodeModeSurfaces, threadId]);

  const envLocked = Boolean(
    activeThread &&
    (activeThread.messages.length > 0 ||
      (activeThread.session !== null && activeThread.session.status !== "closed")),
  );
  const activeTerminalGroup =
    terminalState.terminalGroups.find(
      (group) => group.id === terminalState.activeTerminalGroupId,
    ) ??
    terminalState.terminalGroups.find((group) =>
      group.terminalIds.includes(terminalState.activeTerminalId),
    ) ??
    null;
  const hasReachedSplitLimit =
    (activeTerminalGroup?.terminalIds.length ?? 0) >= MAX_TERMINALS_PER_GROUP;
  const setThreadError = useCallback(
    (targetThreadId: ThreadId | null, error: string | null) => {
      if (!targetThreadId) return;
      if (threads.some((thread) => thread.id === targetThreadId)) {
        setStoreThreadError(targetThreadId, error);
        return;
      }
      setLocalDraftErrorsByThreadId((existing) => {
        if ((existing[targetThreadId] ?? null) === error) {
          return existing;
        }
        return {
          ...existing,
          [targetThreadId]: error,
        };
      });
    },
    [setStoreThreadError, threads],
  );

  const focusComposer = useCallback(() => {
    composerEditorRef.current?.focusAtEnd();
  }, []);
  const scheduleComposerFocus = useCallback(() => {
    window.requestAnimationFrame(() => {
      focusComposer();
    });
  }, [focusComposer]);
  const setTerminalOpen = useCallback(
    (open: boolean) => {
      if (!activeThreadId) return;
      storeSetTerminalOpen(activeThreadId, open);
    },
    [activeThreadId, storeSetTerminalOpen],
  );
  const setTerminalHeight = useCallback(
    (height: number) => {
      if (!activeThreadId) return;
      storeSetTerminalHeight(activeThreadId, height);
    },
    [activeThreadId, storeSetTerminalHeight],
  );
  const toggleTerminalVisibility = useCallback(() => {
    if (!activeThreadId) return;
    setTerminalOpen(!terminalState.terminalOpen);
  }, [activeThreadId, setTerminalOpen, terminalState.terminalOpen]);
  const splitTerminal = useCallback(() => {
    if (!activeThreadId || hasReachedSplitLimit) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeSplitTerminal(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadId, hasReachedSplitLimit, storeSplitTerminal]);
  const createNewTerminal = useCallback(() => {
    if (!activeThreadId) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeNewTerminal(activeThreadId, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadId, storeNewTerminal]);
  const activateTerminal = useCallback(
    (terminalId: string) => {
      if (!activeThreadId) return;
      storeSetActiveTerminal(activeThreadId, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [activeThreadId, storeSetActiveTerminal],
  );
  const closeTerminal = useCallback(
    (terminalId: string) => {
      const api = readNativeApi();
      if (!activeThreadId || !api) return;
      const isFinalTerminal = terminalState.terminalIds.length <= 1;
      const fallbackExitWrite = () =>
        api.terminal
          .write({ threadId: activeThreadId, terminalId, data: "exit\n" })
          .catch(() => undefined);
      if ("close" in api.terminal && typeof api.terminal.close === "function") {
        void (async () => {
          if (isFinalTerminal) {
            await api.terminal
              .clear({ threadId: activeThreadId, terminalId })
              .catch(() => undefined);
          }
          await api.terminal.close({
            threadId: activeThreadId,
            terminalId,
            deleteHistory: true,
          });
        })().catch(() => fallbackExitWrite());
      } else {
        void fallbackExitWrite();
      }
      storeCloseTerminal(activeThreadId, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [activeThreadId, storeCloseTerminal, terminalState.terminalIds.length],
  );
  const runProjectScript = useCallback(
    async (
      script: ProjectScript,
      options?: {
        cwd?: string;
        env?: Record<string, string>;
        worktreePath?: string | null;
        preferNewTerminal?: boolean;
        rememberAsLastInvoked?: boolean;
        allowLocalDraftThread?: boolean;
      },
    ) => {
      const api = readNativeApi();
      if (!api || !activeThreadId || !activeProject || !activeThread) return;
      if (!isServerThread && !options?.allowLocalDraftThread) return;
      if (options?.rememberAsLastInvoked !== false) {
        setLastInvokedScriptByProjectId((current) => {
          if (current[activeProject.id] === script.id) return current;
          return { ...current, [activeProject.id]: script.id };
        });
      }
      const targetCwd = options?.cwd ?? gitCwd ?? activeProject.cwd;
      const baseTerminalId =
        terminalState.activeTerminalId ||
        terminalState.terminalIds[0] ||
        DEFAULT_THREAD_TERMINAL_ID;
      const isBaseTerminalBusy = terminalState.runningTerminalIds.includes(baseTerminalId);
      const wantsNewTerminal = Boolean(options?.preferNewTerminal) || isBaseTerminalBusy;
      const shouldCreateNewTerminal = wantsNewTerminal;
      const targetTerminalId = shouldCreateNewTerminal
        ? `terminal-${randomUUID()}`
        : baseTerminalId;

      setTerminalOpen(true);
      if (shouldCreateNewTerminal) {
        storeNewTerminal(activeThreadId, targetTerminalId);
      } else {
        storeSetActiveTerminal(activeThreadId, targetTerminalId);
      }
      setTerminalFocusRequestId((value) => value + 1);

      const runtimeEnv = projectScriptRuntimeEnv({
        project: {
          cwd: activeProject.cwd,
        },
        worktreePath: options?.worktreePath ?? activeThread.worktreePath ?? null,
        ...(options?.env ? { extraEnv: options.env } : {}),
      });
      const openTerminalInput: Parameters<typeof api.terminal.open>[0] = shouldCreateNewTerminal
        ? {
            threadId: activeThreadId,
            terminalId: targetTerminalId,
            cwd: targetCwd,
            env: runtimeEnv,
            cols: SCRIPT_TERMINAL_COLS,
            rows: SCRIPT_TERMINAL_ROWS,
          }
        : {
            threadId: activeThreadId,
            terminalId: targetTerminalId,
            cwd: targetCwd,
            env: runtimeEnv,
          };

      try {
        await api.terminal.open(openTerminalInput);
        await api.terminal.write({
          threadId: activeThreadId,
          terminalId: targetTerminalId,
          data: `${script.command}\r`,
        });
      } catch (error) {
        setThreadError(
          activeThreadId,
          error instanceof Error ? error.message : `Failed to run script "${script.name}".`,
        );
      }
    },
    [
      activeProject,
      activeThread,
      activeThreadId,
      gitCwd,
      isServerThread,
      setTerminalOpen,
      setThreadError,
      storeNewTerminal,
      storeSetActiveTerminal,
      setLastInvokedScriptByProjectId,
      terminalState.activeTerminalId,
      terminalState.runningTerminalIds,
      terminalState.terminalIds,
    ],
  );
  const persistProjectScripts = useCallback(
    async (input: {
      projectId: ProjectId;
      projectCwd: string;
      previousScripts: ProjectScript[];
      nextScripts: ProjectScript[];
      keybinding?: string | null;
      keybindingCommand: KeybindingCommand;
    }) => {
      const api = readNativeApi();
      if (!api) return;

      await api.orchestration.dispatchCommand({
        type: "project.meta.update",
        commandId: newCommandId(),
        projectId: input.projectId,
        scripts: input.nextScripts,
      });

      const keybindingRule = decodeProjectScriptKeybindingRule({
        keybinding: input.keybinding,
        command: input.keybindingCommand,
      });

      if (isElectron && keybindingRule) {
        await api.server.upsertKeybinding(keybindingRule);
        await queryClient.invalidateQueries({ queryKey: serverQueryKeys.all });
      }
    },
    [queryClient],
  );
  const saveProjectScript = useCallback(
    async (input: NewProjectScriptInput) => {
      if (!activeProject) return;
      const nextId = nextProjectScriptId(
        input.name,
        activeProject.scripts.map((script) => script.id),
      );
      const nextScript: ProjectScript = {
        id: nextId,
        name: input.name,
        command: input.command,
        icon: input.icon,
        runOnWorktreeCreate: input.runOnWorktreeCreate,
      };
      const nextScripts = input.runOnWorktreeCreate
        ? [
            ...activeProject.scripts.map((script) =>
              script.runOnWorktreeCreate ? { ...script, runOnWorktreeCreate: false } : script,
            ),
            nextScript,
          ]
        : [...activeProject.scripts, nextScript];

      await persistProjectScripts({
        projectId: activeProject.id,
        projectCwd: activeProject.cwd,
        previousScripts: activeProject.scripts,
        nextScripts,
        keybinding: input.keybinding,
        keybindingCommand: commandForProjectScript(nextId),
      });
    },
    [activeProject, persistProjectScripts],
  );
  const updateProjectScript = useCallback(
    async (scriptId: string, input: NewProjectScriptInput) => {
      if (!activeProject) return;
      const existingScript = activeProject.scripts.find((script) => script.id === scriptId);
      if (!existingScript) {
        throw new Error("Script not found.");
      }

      const updatedScript: ProjectScript = {
        ...existingScript,
        name: input.name,
        command: input.command,
        icon: input.icon,
        runOnWorktreeCreate: input.runOnWorktreeCreate,
      };
      const nextScripts = activeProject.scripts.map((script) =>
        script.id === scriptId
          ? updatedScript
          : input.runOnWorktreeCreate
            ? { ...script, runOnWorktreeCreate: false }
            : script,
      );

      await persistProjectScripts({
        projectId: activeProject.id,
        projectCwd: activeProject.cwd,
        previousScripts: activeProject.scripts,
        nextScripts,
        keybinding: input.keybinding,
        keybindingCommand: commandForProjectScript(scriptId),
      });
    },
    [activeProject, persistProjectScripts],
  );
  const deleteProjectScript = useCallback(
    async (scriptId: string) => {
      if (!activeProject) return;
      const nextScripts = activeProject.scripts.filter((script) => script.id !== scriptId);

      const deletedName = activeProject.scripts.find((s) => s.id === scriptId)?.name;

      try {
        await persistProjectScripts({
          projectId: activeProject.id,
          projectCwd: activeProject.cwd,
          previousScripts: activeProject.scripts,
          nextScripts,
          keybinding: null,
          keybindingCommand: commandForProjectScript(scriptId),
        });
        toastManager.add({
          type: "success",
          title: `Deleted action "${deletedName ?? "Unknown"}"`,
        });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not delete action",
          description: error instanceof Error ? error.message : "An unexpected error occurred.",
        });
      }
    },
    [activeProject, persistProjectScripts],
  );

  const handleInteractionModeChange = useCallback(
    (mode: ProviderInteractionMode) => {
      if (isArchivedThread) return;
      if (mode === interactionMode) return;
      setComposerDraftInteractionMode(threadId, mode);
      if (isLocalDraftThread) {
        setDraftThreadContext(threadId, { interactionMode: mode });
      }
      scheduleComposerFocus();
    },
    [
      isArchivedThread,
      interactionMode,
      isLocalDraftThread,
      scheduleComposerFocus,
      setComposerDraftInteractionMode,
      setDraftThreadContext,
      threadId,
    ],
  );
  const toggleInteractionMode = useCallback(() => {
    handleInteractionModeChange(interactionMode === "plan" ? "default" : "plan");
  }, [handleInteractionModeChange, interactionMode]);
  const togglePlanSidebar = useCallback(() => {
    if (isArchivedThread) {
      return;
    }
    setPlanSidebarOpen((open) => {
      if (open) {
        const turnKey = activePlan?.turnId ?? activeProposedPlan?.turnId ?? null;
        if (turnKey) {
          planSidebarDismissedForTurnRef.current = turnKey;
        }
      } else {
        planSidebarDismissedForTurnRef.current = null;
      }
      return !open;
    });
  }, [activePlan?.turnId, activeProposedPlan?.turnId, isArchivedThread]);

  const persistThreadSettingsForNextTurn = useCallback(
    async (input: {
      threadId: ThreadId;
      createdAt: string;
      model?: string;
      runtimeMode: RuntimeMode;
      interactionMode: ProviderInteractionMode;
    }) => {
      if (!serverThread) {
        return;
      }
      const api = readNativeApi();
      if (!api) {
        return;
      }

      if (input.model !== undefined && input.model !== serverThread.model) {
        await api.orchestration.dispatchCommand({
          type: "thread.meta.update",
          commandId: newCommandId(),
          threadId: input.threadId,
          model: input.model,
        });
      }

      if (input.runtimeMode !== serverThread.runtimeMode) {
        await api.orchestration.dispatchCommand({
          type: "thread.runtime-mode.set",
          commandId: newCommandId(),
          threadId: input.threadId,
          runtimeMode: input.runtimeMode,
          createdAt: input.createdAt,
        });
      }

      if (input.interactionMode !== serverThread.interactionMode) {
        await api.orchestration.dispatchCommand({
          type: "thread.interaction-mode.set",
          commandId: newCommandId(),
          threadId: input.threadId,
          interactionMode: input.interactionMode,
          createdAt: input.createdAt,
        });
      }
    },
    [serverThread],
  );

  // Auto-scroll on new messages
  const messageCount = timelineMessages.length;
  const scrollMessagesToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer) return;
    scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior });
    lastKnownScrollTopRef.current = scrollContainer.scrollTop;
    shouldAutoScrollRef.current = true;
  }, []);
  const cancelPendingStickToBottom = useCallback(() => {
    const pendingFrame = pendingAutoScrollFrameRef.current;
    if (pendingFrame === null) return;
    pendingAutoScrollFrameRef.current = null;
    window.cancelAnimationFrame(pendingFrame);
  }, []);
  const cancelPendingInteractionAnchorAdjustment = useCallback(() => {
    const pendingFrame = pendingInteractionAnchorFrameRef.current;
    if (pendingFrame === null) return;
    pendingInteractionAnchorFrameRef.current = null;
    window.cancelAnimationFrame(pendingFrame);
  }, []);
  const scheduleStickToBottom = useCallback(() => {
    if (pendingAutoScrollFrameRef.current !== null) return;
    pendingAutoScrollFrameRef.current = window.requestAnimationFrame(() => {
      pendingAutoScrollFrameRef.current = null;
      scrollMessagesToBottom();
    });
  }, [scrollMessagesToBottom]);
  const onMessagesClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const scrollContainer = messagesScrollRef.current;
      if (!scrollContainer || !(event.target instanceof Element)) return;

      const trigger = event.target.closest<HTMLElement>(
        "button, summary, [role='button'], [data-scroll-anchor-target]",
      );
      if (!trigger || !scrollContainer.contains(trigger)) return;
      if (trigger.closest("[data-scroll-anchor-ignore]")) return;

      pendingInteractionAnchorRef.current = {
        element: trigger,
        top: trigger.getBoundingClientRect().top,
      };

      cancelPendingInteractionAnchorAdjustment();
      pendingInteractionAnchorFrameRef.current = window.requestAnimationFrame(() => {
        pendingInteractionAnchorFrameRef.current = null;
        const anchor = pendingInteractionAnchorRef.current;
        pendingInteractionAnchorRef.current = null;
        const activeScrollContainer = messagesScrollRef.current;
        if (!anchor || !activeScrollContainer) return;
        if (!anchor.element.isConnected || !activeScrollContainer.contains(anchor.element)) return;

        const nextTop = anchor.element.getBoundingClientRect().top;
        const delta = nextTop - anchor.top;
        if (Math.abs(delta) < 0.5) return;

        activeScrollContainer.scrollTop += delta;
        lastKnownScrollTopRef.current = activeScrollContainer.scrollTop;
      });
    },
    [cancelPendingInteractionAnchorAdjustment],
  );
  const forceStickToBottom = useCallback(() => {
    cancelPendingStickToBottom();
    scrollMessagesToBottom();
    scheduleStickToBottom();
  }, [cancelPendingStickToBottom, scheduleStickToBottom, scrollMessagesToBottom]);
  const onMessagesScroll = useCallback(() => {
    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer) return;
    const currentScrollTop = scrollContainer.scrollTop;
    const isNearBottom = isScrollContainerNearBottom(scrollContainer);

    if (!shouldAutoScrollRef.current && isNearBottom) {
      shouldAutoScrollRef.current = true;
      pendingUserScrollUpIntentRef.current = false;
    } else if (shouldAutoScrollRef.current && pendingUserScrollUpIntentRef.current) {
      const scrolledUp = currentScrollTop < lastKnownScrollTopRef.current - 1;
      if (scrolledUp) {
        shouldAutoScrollRef.current = false;
      }
      pendingUserScrollUpIntentRef.current = false;
    } else if (shouldAutoScrollRef.current && isPointerScrollActiveRef.current) {
      const scrolledUp = currentScrollTop < lastKnownScrollTopRef.current - 1;
      if (scrolledUp) {
        shouldAutoScrollRef.current = false;
      }
    } else if (shouldAutoScrollRef.current && !isNearBottom) {
      // Catch-all for keyboard/assistive scroll interactions.
      const scrolledUp = currentScrollTop < lastKnownScrollTopRef.current - 1;
      if (scrolledUp) {
        shouldAutoScrollRef.current = false;
      }
    }

    setShowScrollToBottom(!shouldAutoScrollRef.current);
    lastKnownScrollTopRef.current = currentScrollTop;
  }, []);
  const onMessagesWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (event.deltaY < 0) {
      pendingUserScrollUpIntentRef.current = true;
    }
  }, []);
  const onMessagesPointerDown = useCallback((_event: React.PointerEvent<HTMLDivElement>) => {
    isPointerScrollActiveRef.current = true;
  }, []);
  const onMessagesPointerUp = useCallback((_event: React.PointerEvent<HTMLDivElement>) => {
    isPointerScrollActiveRef.current = false;
  }, []);
  const onMessagesPointerCancel = useCallback((_event: React.PointerEvent<HTMLDivElement>) => {
    isPointerScrollActiveRef.current = false;
  }, []);
  const onMessagesTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch) return;
    lastTouchClientYRef.current = touch.clientY;
  }, []);
  const onMessagesTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch) return;
    const previousTouchY = lastTouchClientYRef.current;
    if (previousTouchY !== null && touch.clientY > previousTouchY + 1) {
      pendingUserScrollUpIntentRef.current = true;
    }
    lastTouchClientYRef.current = touch.clientY;
  }, []);
  const onMessagesTouchEnd = useCallback((_event: React.TouchEvent<HTMLDivElement>) => {
    lastTouchClientYRef.current = null;
  }, []);
  useEffect(() => {
    return () => {
      cancelPendingStickToBottom();
      cancelPendingInteractionAnchorAdjustment();
    };
  }, [cancelPendingInteractionAnchorAdjustment, cancelPendingStickToBottom]);
  useLayoutEffect(() => {
    if (!activeThread?.id) return;
    shouldAutoScrollRef.current = true;
    scheduleStickToBottom();
    const timeout = window.setTimeout(() => {
      const scrollContainer = messagesScrollRef.current;
      if (!scrollContainer) return;
      if (isScrollContainerNearBottom(scrollContainer)) return;
      scheduleStickToBottom();
    }, 96);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [activeThread?.id, scheduleStickToBottom]);
  useLayoutEffect(() => {
    const composerForm = composerFormRef.current;
    if (!composerForm) return;
    const measureComposerFormWidth = () => composerForm.clientWidth;

    composerFormHeightRef.current = composerForm.getBoundingClientRect().height;
    setIsComposerFooterCompact(
      shouldUseCompactComposerFooter(measureComposerFormWidth(), {
        hasWideActions: composerFooterHasWideActions,
      }),
    );
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const nextCompact = shouldUseCompactComposerFooter(measureComposerFormWidth(), {
        hasWideActions: composerFooterHasWideActions,
      });
      setIsComposerFooterCompact((previous) => (previous === nextCompact ? previous : nextCompact));

      const nextHeight = entry.contentRect.height;
      const previousHeight = composerFormHeightRef.current;
      composerFormHeightRef.current = nextHeight;

      if (previousHeight > 0 && Math.abs(nextHeight - previousHeight) < 0.5) return;
      if (!shouldAutoScrollRef.current) return;
      scheduleStickToBottom();
    });

    observer.observe(composerForm);
    return () => {
      observer.disconnect();
    };
  }, [activeThread?.id, composerFooterHasWideActions, scheduleStickToBottom]);
  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    scheduleStickToBottom();
  }, [messageCount, scheduleStickToBottom]);
  useEffect(() => {
    if (phase !== "running") return;
    if (!shouldAutoScrollRef.current) return;
    scheduleStickToBottom();
  }, [phase, scheduleStickToBottom, timelineEntries]);

  useEffect(() => {
    setExpandedWorkGroups({});
    setPullRequestDialogState(null);
    if (planSidebarOpenOnNextThreadRef.current) {
      planSidebarOpenOnNextThreadRef.current = false;
      setPlanSidebarOpen(true);
    } else {
      setPlanSidebarOpen(false);
    }
    planSidebarDismissedForTurnRef.current = null;
  }, [activeThread?.id]);

  useEffect(() => {
    if (!composerMenuOpen) {
      setComposerHighlightedItemId(null);
      return;
    }
    setComposerHighlightedItemId((existing) =>
      existing && selectableComposerMenuItems.some((item) => item.id === existing)
        ? existing
        : (selectableComposerMenuItems[0]?.id ?? null),
    );
  }, [composerMenuOpen, selectableComposerMenuItems]);

  useEffect(() => {
    setIsRevertingCheckpoint(false);
  }, [activeThread?.id]);

  useEffect(() => {
    if (!activeThread?.id || terminalState.terminalOpen) return;
    const frame = window.requestAnimationFrame(() => {
      focusComposer();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeThread?.id, focusComposer, terminalState.terminalOpen]);

  useEffect(() => {
    composerFilesRef.current = composerFiles;
  }, [composerFiles]);

  useEffect(() => {
    composerImagesRef.current = composerImages;
  }, [composerImages]);

  useEffect(() => {
    if (!activeThread?.id) return;
    if (activeThread.messages.length === 0) {
      return;
    }
    const serverIds = new Set(activeThread.messages.map((message) => message.id));
    const removedMessages = optimisticUserMessages.filter((message) => serverIds.has(message.id));
    if (removedMessages.length === 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setOptimisticUserMessages((existing) =>
        existing.filter((message) => !serverIds.has(message.id)),
      );
    }, 0);
    for (const removedMessage of removedMessages) {
      const previewUrls = collectUserMessageBlobPreviewUrls(removedMessage);
      if (previewUrls.length > 0) {
        handoffAttachmentPreviews(removedMessage.id, previewUrls);
        continue;
      }
      revokeUserMessagePreviewUrls(removedMessage);
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [activeThread?.id, activeThread?.messages, handoffAttachmentPreviews, optimisticUserMessages]);

  useEffect(() => {
    promptRef.current = prompt;
    setComposerCursor((existing) => clampCollapsedComposerCursor(prompt, existing));
  }, [prompt]);

  useEffect(() => {
    setOptimisticUserMessages((existing) => {
      for (const message of existing) {
        revokeUserMessagePreviewUrls(message);
      }
      return [];
    });
    setSendPhase("idle");
    setSendStartedAt(null);
    setComposerHighlightedItemId(null);
    setComposerCursor(collapseExpandedComposerCursor(promptRef.current, promptRef.current.length));
    setComposerTrigger(detectComposerTriggerForView(promptRef.current, promptRef.current.length));
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
    setExpandedImage(null);
  }, [detectComposerTriggerForView, threadId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (composerImages.length === 0) {
        clearComposerDraftPersistedAttachments(threadId);
        return;
      }
      const getPersistedAttachmentsForThread = () =>
        useComposerDraftStore.getState().draftsByThreadId[threadId]?.persistedAttachments ?? [];
      try {
        const currentPersistedAttachments = getPersistedAttachmentsForThread();
        const existingPersistedById = new Map(
          currentPersistedAttachments.map((attachment) => [attachment.id, attachment]),
        );
        const stagedAttachmentById = new Map<string, PersistedComposerImageAttachment>();
        await Promise.all(
          composerImages.map(async (image) => {
            try {
              const dataUrl = await readFileAsDataUrl(image.file);
              stagedAttachmentById.set(image.id, {
                id: image.id,
                name: image.name,
                mimeType: image.mimeType,
                sizeBytes: image.sizeBytes,
                dataUrl,
              });
            } catch {
              const existingPersisted = existingPersistedById.get(image.id);
              if (existingPersisted) {
                stagedAttachmentById.set(image.id, existingPersisted);
              }
            }
          }),
        );
        const serialized = Array.from(stagedAttachmentById.values());
        if (cancelled) {
          return;
        }
        // Stage attachments in persisted draft state first so persist middleware can write them.
        syncComposerDraftPersistedAttachments(threadId, serialized);
      } catch {
        const currentImageIds = new Set(composerImages.map((image) => image.id));
        const fallbackPersistedAttachments = getPersistedAttachmentsForThread();
        const fallbackPersistedIds = fallbackPersistedAttachments
          .map((attachment) => attachment.id)
          .filter((id) => currentImageIds.has(id));
        const fallbackPersistedIdSet = new Set(fallbackPersistedIds);
        const fallbackAttachments = fallbackPersistedAttachments.filter((attachment) =>
          fallbackPersistedIdSet.has(attachment.id),
        );
        if (cancelled) {
          return;
        }
        syncComposerDraftPersistedAttachments(threadId, fallbackAttachments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    clearComposerDraftPersistedAttachments,
    composerImages,
    syncComposerDraftPersistedAttachments,
    threadId,
  ]);

  const closeExpandedImage = useCallback(() => {
    setExpandedImage(null);
  }, []);
  const navigateExpandedImage = useCallback((direction: -1 | 1) => {
    setExpandedImage((existing) => {
      if (!existing || existing.images.length <= 1) {
        return existing;
      }
      const nextIndex =
        (existing.index + direction + existing.images.length) % existing.images.length;
      if (nextIndex === existing.index) {
        return existing;
      }
      return { ...existing, index: nextIndex };
    });
  }, []);

  useEffect(() => {
    if (!expandedImage) {
      return;
    }

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeExpandedImage();
        return;
      }
      if (expandedImage.images.length <= 1) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        event.stopPropagation();
        navigateExpandedImage(-1);
        return;
      }
      if (event.key !== "ArrowRight") return;
      event.preventDefault();
      event.stopPropagation();
      navigateExpandedImage(1);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeExpandedImage, expandedImage, navigateExpandedImage]);

  const activeWorktreePath = activeThread?.worktreePath;
  const envMode: DraftThreadEnvMode = activeWorktreePath
    ? "worktree"
    : isLocalDraftThread
      ? (draftThread?.envMode ?? "local")
      : "local";

  useEffect(() => {
    if (phase !== "running") return;
    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [phase]);

  const beginSendPhase = useCallback((nextPhase: Exclude<SendPhase, "idle">) => {
    setSendStartedAt((current) => current ?? new Date().toISOString());
    setSendPhase(nextPhase);
  }, []);

  const resetSendPhase = useCallback(() => {
    setSendPhase("idle");
    setSendStartedAt(null);
  }, []);

  useEffect(() => {
    if (sendPhase === "idle") {
      return;
    }
    if (
      phase === "running" ||
      activePendingApproval !== null ||
      activePendingUserInput !== null ||
      activeThread?.error
    ) {
      resetSendPhase();
    }
  }, [
    activePendingApproval,
    activePendingUserInput,
    activeThread?.error,
    phase,
    resetSendPhase,
    sendPhase,
  ]);

  useEffect(() => {
    if (!activeThreadId) return;
    const previous = terminalOpenByThreadRef.current[activeThreadId] ?? false;
    const current = Boolean(terminalState.terminalOpen);

    if (!previous && current) {
      terminalOpenByThreadRef.current[activeThreadId] = current;
      setTerminalFocusRequestId((value) => value + 1);
      return;
    } else if (previous && !current) {
      terminalOpenByThreadRef.current[activeThreadId] = current;
      const frame = window.requestAnimationFrame(() => {
        focusComposer();
      });
      return () => {
        window.cancelAnimationFrame(frame);
      };
    }

    terminalOpenByThreadRef.current[activeThreadId] = current;
  }, [activeThreadId, focusComposer, terminalState.terminalOpen]);

  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      if (!activeThreadId || event.defaultPrevented) return;
      const shortcutContext = {
        terminalFocus: isTerminalFocused(),
        terminalOpen: Boolean(terminalState.terminalOpen),
      };

      const command = resolveShortcutCommand(event, keybindings, {
        context: shortcutContext,
      });
      if (!command) return;

      const shortcutAction = resolveChatViewShortcutAction(command, operatorsMode, {
        diffEnabled: showCodeModeSurfaces,
      });
      if (shortcutAction === "block") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (shortcutAction === "terminal.toggle") {
        event.preventDefault();
        event.stopPropagation();
        toggleTerminalVisibility();
        return;
      }

      if (shortcutAction === "terminal.split") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) {
          setTerminalOpen(true);
        }
        splitTerminal();
        return;
      }

      if (shortcutAction === "terminal.close") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) return;
        closeTerminal(terminalState.activeTerminalId);
        return;
      }

      if (shortcutAction === "terminal.new") {
        event.preventDefault();
        event.stopPropagation();
        if (!terminalState.terminalOpen) {
          setTerminalOpen(true);
        }
        createNewTerminal();
        return;
      }

      if (shortcutAction === "diff.toggle") {
        event.preventDefault();
        event.stopPropagation();
        onToggleDiff();
        return;
      }

      const scriptId = projectScriptIdFromCommand(command);
      if (!scriptId || !activeProject) return;
      const script = activeProject.scripts.find((entry) => entry.id === scriptId);
      if (!script) return;
      event.preventDefault();
      event.stopPropagation();
      void runProjectScript(script);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    activeProject,
    terminalState.terminalOpen,
    terminalState.activeTerminalId,
    activeThreadId,
    closeTerminal,
    createNewTerminal,
    setTerminalOpen,
    runProjectScript,
    splitTerminal,
    keybindings,
    onToggleDiff,
    operatorsMode,
    showCodeModeSurfaces,
    toggleTerminalVisibility,
  ]);

  const addComposerImages = (files: File[]) => {
    if (!activeThreadId || files.length === 0) return;
    if (isArchivedThread) {
      return;
    }
    if (isSendBusy || sendInFlightRef.current) {
      return;
    }

    if (pendingUserInputs.length > 0) {
      toastManager.add({
        type: "error",
        title: "Attach images after answering plan questions.",
      });
      return;
    }

    const nextImages: ComposerImageAttachment[] = [];
    let nextImageCount = composerImagesRef.current.length;
    let error: string | null = null;
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        error = `Unsupported file type for '${file.name}'. Please attach image files only.`;
        continue;
      }
      if (file.size > PROVIDER_SEND_TURN_MAX_IMAGE_BYTES) {
        error = `'${file.name}' exceeds the ${IMAGE_SIZE_LIMIT_LABEL} attachment limit.`;
        continue;
      }
      if (nextImageCount >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
        error = `You can attach up to ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} images per message.`;
        break;
      }

      const previewUrl = URL.createObjectURL(file);
      nextImages.push({
        type: "image",
        id: randomUUID(),
        name: file.name || "image",
        mimeType: file.type,
        sizeBytes: file.size,
        previewUrl,
        file,
      });
      nextImageCount += 1;
    }

    if (nextImages.length === 1 && nextImages[0]) {
      addComposerImage(nextImages[0]);
    } else if (nextImages.length > 1) {
      addComposerImagesToDraft(nextImages);
    }
    setThreadError(activeThreadId, error);
  };

  const addComposerFiles = (files: File[]) => {
    if (!activeThreadId || files.length === 0) return;
    if (!operatorsMode) {
      return;
    }
    if (isArchivedThread) {
      return;
    }
    if (isSendBusy || sendInFlightRef.current) {
      return;
    }

    if (pendingUserInputs.length > 0) {
      toastManager.add({
        type: "error",
        title: "Upload files after answering plan questions.",
      });
      return;
    }

    const nextFiles: ComposerFileAttachment[] = [];
    let nextFileCount = composerFilesRef.current.length;
    let nextTotalSizeBytes = composerFilesRef.current.reduce(
      (total, file) => total + file.sizeBytes,
      0,
    );
    let error: string | null = null;

    for (const file of files) {
      if (file.size > OPERATORS_SESSION_FILE_MAX_BYTES) {
        error = `'${file.name}' exceeds the ${SESSION_FILE_SIZE_LIMIT_LABEL} file limit.`;
        continue;
      }
      if (nextFileCount >= OPERATORS_SESSION_FILE_MAX_FILES) {
        error = `You can upload up to ${OPERATORS_SESSION_FILE_MAX_FILES} files per message.`;
        break;
      }
      if (nextTotalSizeBytes + file.size > OPERATORS_SESSION_FILE_MAX_TOTAL_BYTES) {
        error = `Uploaded files can total up to ${SESSION_TOTAL_FILE_SIZE_LIMIT_LABEL} per message.`;
        break;
      }

      nextFiles.push({
        id: randomUUID(),
        name: file.name || "file",
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        file,
      });
      nextFileCount += 1;
      nextTotalSizeBytes += file.size;
    }

    if (nextFiles.length === 1 && nextFiles[0]) {
      addComposerFile(nextFiles[0]);
    } else if (nextFiles.length > 1) {
      addComposerFilesToDraft(nextFiles);
    }

    if (error) {
      toastManager.add({
        type: "error",
        title: "Could not stage all files",
        description: error,
      });
    }
  };

  const removeComposerImage = (imageId: string) => {
    removeComposerImageFromDraft(imageId);
  };

  const removeComposerFile = (fileId: string) => {
    removeComposerFileFromDraft(fileId);
  };

  const onComposerPaste = (event: React.ClipboardEvent<HTMLElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) {
      return;
    }
    if (isArchivedThread) {
      event.preventDefault();
      return;
    }
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const genericFiles = operatorsMode
      ? files.filter((file) => !file.type.startsWith("image/"))
      : [];
    if (imageFiles.length === 0 && genericFiles.length === 0) {
      return;
    }
    event.preventDefault();
    addComposerImages(imageFiles);
    addComposerFiles(genericFiles);
  };

  const onComposerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    if (isArchivedThread) {
      dragDepthRef.current = 0;
      setIsDragOverComposer(false);
      return;
    }
    dragDepthRef.current += 1;
    setIsDragOverComposer(true);
  };

  const onComposerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    if (isArchivedThread) {
      event.dataTransfer.dropEffect = "none";
      setIsDragOverComposer(false);
      return;
    }
    event.dataTransfer.dropEffect = "copy";
    setIsDragOverComposer(true);
  };

  const onComposerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOverComposer(false);
    }
  };

  const onComposerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) {
      return;
    }
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
    if (isArchivedThread) {
      return;
    }
    const files = Array.from(event.dataTransfer.files);
    addComposerImages(files.filter((file) => file.type.startsWith("image/")));
    addComposerFiles(files.filter((file) => !file.type.startsWith("image/")));
    focusComposer();
  };

  const openComposerFilePicker = () => {
    if (isArchivedThread || isConnecting || isComposerApprovalState) {
      return;
    }
    composerFileInputRef.current?.click();
  };

  const onComposerFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    if (files.length > 0) {
      addComposerFiles(files);
    }
    event.currentTarget.value = "";
  };

  const onRevertToTurnCount = useCallback(
    async (turnCount: number) => {
      const api = readNativeApi();
      if (!api || !activeThread || isRevertingCheckpoint) return;

      if (phase === "running" || isSendBusy || isConnecting) {
        setThreadError(activeThread.id, "Interrupt the current turn before reverting checkpoints.");
        return;
      }
      const confirmed = await api.dialogs.confirm(
        [
          `Revert this thread to checkpoint ${turnCount}?`,
          "This will discard newer messages and turn diffs in this thread.",
          "This action cannot be undone.",
        ].join("\n"),
      );
      if (!confirmed) {
        return;
      }

      setIsRevertingCheckpoint(true);
      setThreadError(activeThread.id, null);
      try {
        await api.orchestration.dispatchCommand({
          type: "thread.checkpoint.revert",
          commandId: newCommandId(),
          threadId: activeThread.id,
          turnCount,
          createdAt: new Date().toISOString(),
        });
      } catch (err) {
        setThreadError(
          activeThread.id,
          err instanceof Error ? err.message : "Failed to revert thread state.",
        );
      }
      setIsRevertingCheckpoint(false);
    },
    [activeThread, isConnecting, isRevertingCheckpoint, isSendBusy, phase, setThreadError],
  );

  const onSend = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    const api = readNativeApi();
    if (
      !api ||
      !activeThread ||
      isArchivedThread ||
      isSendBusy ||
      isConnecting ||
      sendInFlightRef.current
    ) {
      return;
    }
    if (activePendingProgress) {
      onAdvanceActivePendingUserInput();
      return;
    }
    const promptSnapshot = prompt;
    const trimmed = promptSnapshot.trim();
    if (showPlanFollowUpPrompt && activeProposedPlan) {
      const followUp = resolvePlanFollowUpSubmission({
        draftText: trimmed,
        planMarkdown: activeProposedPlan.planMarkdown,
      });
      await onSubmitPlanFollowUp({
        files: composerFiles,
        images: composerImages,
        text: followUp.text,
        interactionMode: followUp.interactionMode,
      });
      return;
    }
    const standaloneSlashCommand =
      composerImages.length === 0 && composerFiles.length === 0
        ? parseStandaloneComposerSlashCommand(trimmed)
        : null;
    if (standaloneSlashCommand) {
      await handleInteractionModeChange(standaloneSlashCommand);
      clearComposerPromptState(activeThread.id);
      return;
    }
    const skillNames =
      operatorsMode && selectedProvider === "codex"
        ? parseComposerSkillInvocations(
            promptSnapshot,
            sessionSkills.filter((skill) => skill.availableInSession).map((skill) => skill.name),
          )
        : [];
    if (!trimmed && composerImages.length === 0 && composerFiles.length === 0) return;
    if (!activeProject) return;
    const threadIdForSend = activeThread.id;
    if (composerFiles.length > 0 && !operatorsSessionFileTransferId) {
      setThreadError(
        threadIdForSend,
        "File uploads require an active Operators session for this thread.",
      );
      return;
    }
    const isFirstMessage = !isServerThread || activeThread.messages.length === 0;
    const baseBranchForWorktree =
      isFirstMessage && envMode === "worktree" && !activeThread.worktreePath
        ? activeThread.branch
        : null;

    // In worktree mode, require an explicit base branch so we don't silently
    // fall back to local execution when branch selection is missing.
    const shouldCreateWorktree =
      isFirstMessage && envMode === "worktree" && !activeThread.worktreePath;
    if (shouldCreateWorktree && !activeThread.branch) {
      setStoreThreadError(
        threadIdForSend,
        "Select a base branch before sending in New worktree mode.",
      );
      return;
    }

    clearComposerPromptState();
    sendInFlightRef.current = true;
    beginSendPhase(baseBranchForWorktree ? "preparing-worktree" : "sending-turn");

    const composerFilesSnapshot = [...composerFiles];
    const composerImagesSnapshot = [...composerImages];
    const messageIdForSend = newMessageId();
    const messageCreatedAt = new Date().toISOString();
    const optimisticAttachments = composerImagesSnapshot.map((image) => ({
      type: "image" as const,
      id: image.id,
      name: image.name,
      mimeType: image.mimeType,
      sizeBytes: image.sizeBytes,
      previewUrl: image.previewUrl,
    }));
    setThreadError(threadIdForSend, null);

    let createdServerThreadForLocalDraft = false;
    let optimisticMessageQueued = false;
    let turnStartSucceeded = false;
    let nextThreadBranch = activeThread.branch;
    let nextThreadWorktreePath = activeThread.worktreePath;
    await (async () => {
      const turnAttachmentsPromise = buildTurnImageAttachments(composerImagesSnapshot);

      // On first message: lock in branch + create worktree if needed.
      if (baseBranchForWorktree) {
        beginSendPhase("preparing-worktree");
        const newBranch = buildTemporaryWorktreeBranchName();
        const result = await createWorktreeMutation.mutateAsync({
          cwd: activeProject.cwd,
          branch: baseBranchForWorktree,
          newBranch,
        });
        nextThreadBranch = result.worktree.branch;
        nextThreadWorktreePath = result.worktree.path;
        if (isServerThread) {
          await api.orchestration.dispatchCommand({
            type: "thread.meta.update",
            commandId: newCommandId(),
            threadId: threadIdForSend,
            branch: result.worktree.branch,
            worktreePath: result.worktree.path,
          });
          // Keep local thread state in sync immediately so terminal drawer opens
          // with the worktree cwd/env instead of briefly using the project root.
          setStoreThreadBranch(threadIdForSend, result.worktree.branch, result.worktree.path);
        }
      }

      let firstComposerImageName: string | null = null;
      if (composerImagesSnapshot.length > 0) {
        const firstComposerImage = composerImagesSnapshot[0];
        if (firstComposerImage) {
          firstComposerImageName = firstComposerImage.name;
        }
      }
      let firstComposerFileName: string | null = null;
      if (composerFilesSnapshot.length > 0) {
        const firstComposerFile = composerFilesSnapshot[0];
        if (firstComposerFile) {
          firstComposerFileName = firstComposerFile.name;
        }
      }
      let titleSeed = trimmed;
      if (!titleSeed) {
        if (firstComposerImageName) {
          titleSeed = `Image: ${firstComposerImageName}`;
        } else if (firstComposerFileName) {
          titleSeed = `File: ${firstComposerFileName}`;
        } else {
          titleSeed = "New thread";
        }
      }
      const title = truncateTitle(titleSeed);
      const threadCreateModel = resolveNewThreadModel(
        selectedModel || (activeProject.model as ModelSlug) || DEFAULT_MODEL_BY_PROVIDER.codex,
      ) as ModelSlug;

      if (isLocalDraftThread) {
        await api.orchestration.dispatchCommand({
          type: "thread.create",
          commandId: newCommandId(),
          threadId: threadIdForSend,
          projectId: activeProject.id,
          title,
          model: threadCreateModel,
          runtimeMode,
          interactionMode,
          branch: nextThreadBranch,
          worktreePath: nextThreadWorktreePath,
          createdAt: activeThread.createdAt,
        });
        createdServerThreadForLocalDraft = true;
      }

      let setupScript: ProjectScript | null = null;
      if (baseBranchForWorktree) {
        setupScript = setupProjectScript(activeProject.scripts);
      }
      if (setupScript) {
        let shouldRunSetupScript = false;
        if (isServerThread) {
          shouldRunSetupScript = true;
        } else {
          if (createdServerThreadForLocalDraft) {
            shouldRunSetupScript = true;
          }
        }
        if (shouldRunSetupScript) {
          const setupScriptOptions: Parameters<typeof runProjectScript>[1] = {
            worktreePath: nextThreadWorktreePath,
            rememberAsLastInvoked: false,
            allowLocalDraftThread: createdServerThreadForLocalDraft,
          };
          if (nextThreadWorktreePath) {
            setupScriptOptions.cwd = nextThreadWorktreePath;
          }
          await runProjectScript(setupScript, setupScriptOptions);
        }
      }

      // Auto-title from first message
      if (isFirstMessage && isServerThread) {
        await api.orchestration.dispatchCommand({
          type: "thread.meta.update",
          commandId: newCommandId(),
          threadId: threadIdForSend,
          title,
        });
      }

      if (isServerThread) {
        await persistThreadSettingsForNextTurn({
          threadId: threadIdForSend,
          createdAt: messageCreatedAt,
          ...(selectedModel ? { model: selectedModel } : {}),
          runtimeMode,
          interactionMode,
        });
      }

      beginSendPhase("sending-turn");
      const turnAttachments = await turnAttachmentsPromise;
      const uploadedFileReferences =
        composerFilesSnapshot.length > 0 && operatorsSessionFileTransferId
          ? await uploadWorkspaceFileReferences(threadIdForSend, composerFilesSnapshot)
          : [];

      setOptimisticUserMessages((existing) => [
        ...existing,
        {
          id: messageIdForSend,
          role: "user",
          ...(optimisticMessageAuthorName
            ? {
                author: {
                  email: optimisticMessageAuthorEmail,
                  name: optimisticMessageAuthorName,
                },
              }
            : {}),
          text: promptSnapshot,
          ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
          ...(uploadedFileReferences.length > 0 ? { uploadedFiles: uploadedFileReferences } : {}),
          createdAt: messageCreatedAt,
          streaming: false,
        },
      ]);
      optimisticMessageQueued = true;
      shouldAutoScrollRef.current = true;
      forceStickToBottom();

      await api.orchestration.dispatchCommand({
        type: "thread.turn.start",
        commandId: newCommandId(),
        threadId: threadIdForSend,
        message: {
          messageId: messageIdForSend,
          role: "user",
          text: promptSnapshot,
          ...(optimisticMessageAuthorName
            ? {
                author: {
                  email: optimisticMessageAuthorEmail,
                  name: optimisticMessageAuthorName,
                },
              }
            : {}),
          attachments: turnAttachments,
          ...(uploadedFileReferences.length > 0 ? { uploadedFiles: uploadedFileReferences } : {}),
        },
        ...(skillNames.length > 0 ? { skillNames } : {}),
        model: isLocalDraftThread ? threadCreateModel : selectedModel || undefined,
        ...(selectedModelOptionsForDispatch
          ? { modelOptions: selectedModelOptionsForDispatch }
          : {}),
        ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
        provider: selectedProvider,
        assistantDeliveryMode: settings.enableAssistantStreaming ? "streaming" : "buffered",
        runtimeMode,
        interactionMode,
        createdAt: messageCreatedAt,
      });
      turnStartSucceeded = true;
      clearComposerPromptState(threadIdForSend);
    })().catch(async (err: unknown) => {
      if (createdServerThreadForLocalDraft && !turnStartSucceeded) {
        await api.orchestration
          .dispatchCommand({
            type: "thread.delete",
            commandId: newCommandId(),
            threadId: threadIdForSend,
          })
          .catch(() => undefined);
      }
      if (optimisticMessageQueued && !turnStartSucceeded) {
        setOptimisticUserMessages((existing) => {
          const removed = existing.filter((message) => message.id === messageIdForSend);
          for (const message of removed) {
            revokeUserMessagePreviewUrls(message);
          }
          const next = existing.filter((message) => message.id !== messageIdForSend);
          return next.length === existing.length ? existing : next;
        });
      }
      if (!turnStartSucceeded) {
        restoreComposerPromptState(promptSnapshot);
      }
      setThreadError(
        threadIdForSend,
        err instanceof Error ? err.message : "Failed to send message.",
      );
    });
    sendInFlightRef.current = false;
    if (!turnStartSucceeded) {
      resetSendPhase();
    }
  };

  const onInterrupt = async () => {
    const api = readNativeApi();
    if (!api || !activeThread || isArchivedThread) return;
    await api.orchestration.dispatchCommand({
      type: "thread.turn.interrupt",
      commandId: newCommandId(),
      threadId: activeThread.id,
      createdAt: new Date().toISOString(),
    });
  };

  const onRespondToApproval = useCallback(
    async (requestId: ApprovalRequestId, decision: ProviderApprovalDecision) => {
      const api = readNativeApi();
      if (!api || !activeThreadId || isArchivedThread) return;

      setRespondingRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      await api.orchestration
        .dispatchCommand({
          type: "thread.approval.respond",
          commandId: newCommandId(),
          threadId: activeThreadId,
          requestId,
          decision,
          createdAt: new Date().toISOString(),
        })
        .catch((err: unknown) => {
          setStoreThreadError(
            activeThreadId,
            err instanceof Error ? err.message : "Failed to submit approval decision.",
          );
        });
      setRespondingRequestIds((existing) => existing.filter((id) => id !== requestId));
    },
    [activeThreadId, isArchivedThread, setStoreThreadError],
  );

  const onRespondToUserInput = useCallback(
    async (requestId: ApprovalRequestId, answers: Record<string, unknown>) => {
      const api = readNativeApi();
      if (!api || !activeThreadId || isArchivedThread) return;

      setRespondingUserInputRequestIds((existing) =>
        existing.includes(requestId) ? existing : [...existing, requestId],
      );
      await api.orchestration
        .dispatchCommand({
          type: "thread.user-input.respond",
          commandId: newCommandId(),
          threadId: activeThreadId,
          requestId,
          answers,
          createdAt: new Date().toISOString(),
        })
        .catch((err: unknown) => {
          setStoreThreadError(
            activeThreadId,
            err instanceof Error ? err.message : "Failed to submit user input.",
          );
        });
      setRespondingUserInputRequestIds((existing) => existing.filter((id) => id !== requestId));
    },
    [activeThreadId, isArchivedThread, setStoreThreadError],
  );

  const setActivePendingUserInputQuestionIndex = useCallback(
    (nextQuestionIndex: number) => {
      if (!activePendingUserInput) {
        return;
      }
      setPendingUserInputQuestionIndexByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: nextQuestionIndex,
      }));
    },
    [activePendingUserInput],
  );

  const onSelectActivePendingUserInputOption = useCallback(
    (questionId: string, optionLabel: string) => {
      if (!activePendingUserInput || isArchivedThread) {
        return;
      }
      setPendingUserInputAnswersByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: {
          ...existing[activePendingUserInput.requestId],
          [questionId]: {
            selectedOptionLabel: optionLabel,
            customAnswer: "",
          },
        },
      }));
      promptRef.current = "";
      setComposerCursor(0);
      setComposerTrigger(null);
    },
    [activePendingUserInput, isArchivedThread],
  );

  const onChangeActivePendingUserInputCustomAnswer = useCallback(
    (
      questionId: string,
      value: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
    ) => {
      if (!activePendingUserInput || isArchivedThread) {
        return;
      }
      promptRef.current = value;
      setPendingUserInputAnswersByRequestId((existing) => ({
        ...existing,
        [activePendingUserInput.requestId]: {
          ...existing[activePendingUserInput.requestId],
          [questionId]: setPendingUserInputCustomAnswer(
            existing[activePendingUserInput.requestId]?.[questionId],
            value,
          ),
        },
      }));
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTriggerForView(value, expandedCursor),
      );
    },
    [activePendingUserInput, detectComposerTriggerForView, isArchivedThread],
  );

  const onAdvanceActivePendingUserInput = useCallback(() => {
    if (!activePendingUserInput || !activePendingProgress || isArchivedThread) {
      return;
    }
    if (activePendingProgress.isLastQuestion) {
      if (activePendingResolvedAnswers) {
        void onRespondToUserInput(activePendingUserInput.requestId, activePendingResolvedAnswers);
      }
      return;
    }
    setActivePendingUserInputQuestionIndex(activePendingProgress.questionIndex + 1);
  }, [
    activePendingProgress,
    activePendingResolvedAnswers,
    activePendingUserInput,
    isArchivedThread,
    onRespondToUserInput,
    setActivePendingUserInputQuestionIndex,
  ]);

  const onPreviousActivePendingUserInputQuestion = useCallback(() => {
    if (!activePendingProgress || isArchivedThread) {
      return;
    }
    setActivePendingUserInputQuestionIndex(Math.max(activePendingProgress.questionIndex - 1, 0));
  }, [activePendingProgress, isArchivedThread, setActivePendingUserInputQuestionIndex]);

  const onSubmitPlanFollowUp = useCallback(
    async ({
      files,
      images,
      text,
      interactionMode: nextInteractionMode,
    }: {
      files: ComposerFileAttachment[];
      images: ComposerImageAttachment[];
      text: string;
      interactionMode: "default" | "plan";
    }) => {
      const api = readNativeApi();
      if (
        !api ||
        !activeThread ||
        isArchivedThread ||
        !isServerThread ||
        isSendBusy ||
        isConnecting ||
        sendInFlightRef.current
      ) {
        return;
      }

      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }
      const skillNames =
        operatorsMode && selectedProvider === "codex"
          ? parseComposerSkillInvocations(
              text,
              sessionSkills.filter((skill) => skill.availableInSession).map((skill) => skill.name),
            )
          : [];

      const threadIdForSend = activeThread.id;
      const messageIdForSend = newMessageId();
      const messageCreatedAt = new Date().toISOString();
      const optimisticAttachments = images.map((image) => ({
        type: "image" as const,
        id: image.id,
        name: image.name,
        mimeType: image.mimeType,
        sizeBytes: image.sizeBytes,
        previewUrl: image.previewUrl,
      }));

      sendInFlightRef.current = true;
      beginSendPhase("sending-turn");
      setThreadError(threadIdForSend, null);

      try {
        await persistThreadSettingsForNextTurn({
          threadId: threadIdForSend,
          createdAt: messageCreatedAt,
          ...(selectedModel ? { model: selectedModel } : {}),
          runtimeMode,
          interactionMode: nextInteractionMode,
        });

        const turnAttachments = await buildTurnImageAttachments(images);
        const uploadedFileReferences = await uploadWorkspaceFileReferences(threadIdForSend, files);

        setOptimisticUserMessages((existing) => [
          ...existing,
          {
            id: messageIdForSend,
            role: "user",
            ...(optimisticMessageAuthorName
              ? {
                  author: {
                    email: optimisticMessageAuthorEmail,
                    name: optimisticMessageAuthorName,
                  },
                }
              : {}),
            text,
            ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
            ...(uploadedFileReferences.length > 0 ? { uploadedFiles: uploadedFileReferences } : {}),
            createdAt: messageCreatedAt,
            streaming: false,
          },
        ]);
        shouldAutoScrollRef.current = true;
        forceStickToBottom();

        await api.orchestration.dispatchCommand({
          type: "thread.turn.start",
          commandId: newCommandId(),
          threadId: threadIdForSend,
          message: {
            messageId: messageIdForSend,
            role: "user",
            text,
            ...(optimisticMessageAuthorName
              ? {
                  author: {
                    email: optimisticMessageAuthorEmail,
                    name: optimisticMessageAuthorName,
                  },
                }
              : {}),
            attachments: turnAttachments,
            ...(uploadedFileReferences.length > 0 ? { uploadedFiles: uploadedFileReferences } : {}),
          },
          ...(skillNames.length > 0 ? { skillNames } : {}),
          provider: selectedProvider,
          model: selectedModel || undefined,
          ...(selectedModelOptionsForDispatch
            ? { modelOptions: selectedModelOptionsForDispatch }
            : {}),
          ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
          assistantDeliveryMode: settings.enableAssistantStreaming ? "streaming" : "buffered",
          runtimeMode,
          interactionMode: nextInteractionMode,
          createdAt: messageCreatedAt,
        });
        // Keep the mode toggle and plan-follow-up banner in sync once the turn
        // has been accepted for execution.
        setComposerDraftInteractionMode(threadIdForSend, nextInteractionMode);
        // Optimistically open the plan sidebar when implementing (not refining).
        // "default" mode here means the agent is executing the plan, which produces
        // step-tracking activities that the sidebar will display.
        if (nextInteractionMode === "default") {
          planSidebarDismissedForTurnRef.current = null;
          setPlanSidebarOpen(true);
        }
        clearComposerPromptState(threadIdForSend);
        sendInFlightRef.current = false;
      } catch (err) {
        setOptimisticUserMessages((existing) =>
          existing.filter((message) => message.id !== messageIdForSend),
        );
        setThreadError(
          threadIdForSend,
          err instanceof Error ? err.message : "Failed to send plan follow-up.",
        );
        sendInFlightRef.current = false;
        resetSendPhase();
      }
    },
    [
      activeThread,
      beginSendPhase,
      buildTurnImageAttachments,
      clearComposerPromptState,
      forceStickToBottom,
      isArchivedThread,
      isConnecting,
      isSendBusy,
      isServerThread,
      operatorsMode,
      optimisticMessageAuthorEmail,
      optimisticMessageAuthorName,
      persistThreadSettingsForNextTurn,
      resetSendPhase,
      runtimeMode,
      selectedModel,
      selectedModelOptionsForDispatch,
      providerOptionsForDispatch,
      selectedProvider,
      sessionSkills,
      setComposerDraftInteractionMode,
      setThreadError,
      settings.enableAssistantStreaming,
      uploadWorkspaceFileReferences,
    ],
  );

  const onImplementPlanInNewThread = useCallback(async () => {
    const api = readNativeApi();
    if (
      !api ||
      !activeThread ||
      !activeProject ||
      !activeProposedPlan ||
      isArchivedThread ||
      !isServerThread ||
      isSendBusy ||
      isConnecting ||
      sendInFlightRef.current
    ) {
      return;
    }

    const createdAt = new Date().toISOString();
    const nextThreadId = newThreadId();
    const planMarkdown = activeProposedPlan.planMarkdown;
    const implementationPrompt = buildPlanImplementationPrompt(planMarkdown);
    const nextThreadTitle = truncateTitle(buildPlanImplementationThreadTitle(planMarkdown));
    const nextThreadModel = resolveNewThreadModel(
      selectedModel ||
        (activeThread.model as ModelSlug) ||
        (activeProject.model as ModelSlug) ||
        DEFAULT_MODEL_BY_PROVIDER.codex,
    ) as ModelSlug;
    const threadAttachmentsPromise = buildTurnImageAttachments(composerImages);
    let turnStartSucceeded = false;

    sendInFlightRef.current = true;
    beginSendPhase("sending-turn");
    const finish = () => {
      sendInFlightRef.current = false;
      resetSendPhase();
    };

    try {
      await api.orchestration.dispatchCommand({
        type: "thread.create",
        commandId: newCommandId(),
        threadId: nextThreadId,
        projectId: activeProject.id,
        title: nextThreadTitle,
        model: nextThreadModel,
        runtimeMode,
        interactionMode: "default",
        branch: activeThread.branch,
        worktreePath: activeThread.worktreePath,
        createdAt,
      });

      const turnAttachments = await threadAttachmentsPromise;
      const uploadedFileReferences = await uploadWorkspaceFileReferences(
        nextThreadId,
        composerFiles,
      );

      await api.orchestration.dispatchCommand({
        type: "thread.turn.start",
        commandId: newCommandId(),
        threadId: nextThreadId,
        message: {
          messageId: newMessageId(),
          role: "user",
          text: implementationPrompt,
          attachments: turnAttachments,
          ...(uploadedFileReferences.length > 0 ? { uploadedFiles: uploadedFileReferences } : {}),
        },
        provider: selectedProvider,
        model: nextThreadModel,
        ...(selectedModelOptionsForDispatch
          ? { modelOptions: selectedModelOptionsForDispatch }
          : {}),
        ...(providerOptionsForDispatch ? { providerOptions: providerOptionsForDispatch } : {}),
        assistantDeliveryMode: settings.enableAssistantStreaming ? "streaming" : "buffered",
        runtimeMode,
        interactionMode: "default",
        createdAt,
      });
      turnStartSucceeded = true;

      clearComposerPromptState(activeThread.id);

      // Signal that the plan sidebar should open on the new thread.
      planSidebarOpenOnNextThreadRef.current = true;
      await navigate({
        to: "/operators/$threadId",
        params: { threadId: nextThreadId },
      });
    } catch (err) {
      if (!turnStartSucceeded) {
        await api.orchestration
          .dispatchCommand({
            type: "thread.delete",
            commandId: newCommandId(),
            threadId: nextThreadId,
          })
          .catch(() => undefined);
      }
      toastManager.add({
        type: "error",
        title: "Could not start implementation thread",
        description:
          err instanceof Error ? err.message : "An error occurred while creating the new thread.",
      });
    } finally {
      finish();
    }
  }, [
    activeProject,
    activeProposedPlan,
    activeThread,
    beginSendPhase,
    buildTurnImageAttachments,
    clearComposerPromptState,
    composerFiles,
    composerImages,
    isArchivedThread,
    isConnecting,
    isSendBusy,
    isServerThread,
    navigate,
    resetSendPhase,
    runtimeMode,
    selectedModel,
    selectedModelOptionsForDispatch,
    providerOptionsForDispatch,
    selectedProvider,
    settings.enableAssistantStreaming,
    uploadWorkspaceFileReferences,
  ]);

  const onProviderModelSelect = useCallback(
    (
      provider: ProviderKind,
      model: ModelSlug,
      options?: {
        focusComposer?: boolean;
      },
    ) => {
      if (isArchivedThread) return;
      if (!activeThread) return;
      if (lockedProvider !== null && provider !== lockedProvider) {
        if (options?.focusComposer !== false) {
          scheduleComposerFocus();
        }
        return;
      }
      setComposerDraftProvider(activeThread.id, provider);
      const selection = resolveAppModelSelectionWithReasoning(
        provider,
        settings.customCodexModels,
        model,
      );
      setComposerDraftModel(activeThread.id, selection.model);
      setStickyComposerModel(selection.model);
      if (provider === "codex") {
        setComposerDraftEffort(activeThread.id, selection.reasoningEffort);
      }
      if (options?.focusComposer !== false) {
        scheduleComposerFocus();
      }
    },
    [
      activeThread,
      isArchivedThread,
      lockedProvider,
      scheduleComposerFocus,
      setComposerDraftModel,
      setComposerDraftEffort,
      setComposerDraftProvider,
      setStickyComposerModel,
      settings.customCodexModels,
    ],
  );
  const lockedOperatorsModel = operatorsSurfaceModel(settings.operatorsSurface);
  useEffect(() => {
    if (!operatorsMode || !activeThread || isArchivedThread || selectedProvider !== "codex") {
      return;
    }
    if (composerDraft.model === lockedOperatorsModel) return;
    onProviderModelSelect("codex", lockedOperatorsModel, { focusComposer: false });
  }, [
    activeThread,
    composerDraft.model,
    isArchivedThread,
    lockedOperatorsModel,
    onProviderModelSelect,
    operatorsMode,
    selectedProvider,
  ]);
  const onEffortSelect = useCallback(
    (effort: CodexReasoningEffort) => {
      if (isArchivedThread) return;
      setComposerDraftEffort(threadId, effort);
      scheduleComposerFocus();
    },
    [isArchivedThread, scheduleComposerFocus, setComposerDraftEffort, threadId],
  );
  const onCodexFastModeChange = useCallback(
    (enabled: boolean) => {
      if (isArchivedThread) return;
      setComposerDraftCodexFastMode(threadId, enabled);
      scheduleComposerFocus();
    },
    [isArchivedThread, scheduleComposerFocus, setComposerDraftCodexFastMode, threadId],
  );
  const onEnvModeChange = useCallback(
    (mode: DraftThreadEnvMode) => {
      if (isLocalDraftThread) {
        setDraftThreadContext(threadId, { envMode: mode });
      }
      scheduleComposerFocus();
    },
    [isLocalDraftThread, scheduleComposerFocus, setDraftThreadContext, threadId],
  );

  const applyPromptReplacement = useCallback(
    (
      rangeStart: number,
      rangeEnd: number,
      replacement: string,
      options?: { expectedText?: string },
    ): boolean => {
      const currentText = promptRef.current;
      const safeStart = Math.max(0, Math.min(currentText.length, rangeStart));
      const safeEnd = Math.max(safeStart, Math.min(currentText.length, rangeEnd));
      if (
        options?.expectedText !== undefined &&
        currentText.slice(safeStart, safeEnd) !== options.expectedText
      ) {
        return false;
      }
      const next = replaceTextRange(promptRef.current, rangeStart, rangeEnd, replacement);
      const nextCursor = collapseExpandedComposerCursor(next.text, next.cursor);
      promptRef.current = next.text;
      const activePendingQuestion = activePendingProgress?.activeQuestion;
      if (activePendingQuestion && activePendingUserInput) {
        setPendingUserInputAnswersByRequestId((existing) => ({
          ...existing,
          [activePendingUserInput.requestId]: {
            ...existing[activePendingUserInput.requestId],
            [activePendingQuestion.id]: setPendingUserInputCustomAnswer(
              existing[activePendingUserInput.requestId]?.[activePendingQuestion.id],
              next.text,
            ),
          },
        }));
      } else {
        setPrompt(next.text);
      }
      setComposerCursor(nextCursor);
      setComposerTrigger(
        detectComposerTriggerForView(
          next.text,
          expandCollapsedComposerCursor(next.text, nextCursor),
        ),
      );
      window.requestAnimationFrame(() => {
        composerEditorRef.current?.focusAt(nextCursor);
      });
      return true;
    },
    [
      activePendingProgress?.activeQuestion,
      activePendingUserInput,
      detectComposerTriggerForView,
      setPrompt,
    ],
  );

  const readComposerSnapshot = useCallback((): {
    value: string;
    cursor: number;
    expandedCursor: number;
  } => {
    const editorSnapshot = composerEditorRef.current?.readSnapshot();
    if (editorSnapshot) {
      return editorSnapshot;
    }
    return {
      value: promptRef.current,
      cursor: composerCursor,
      expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
    };
  }, [composerCursor]);

  const resolveActiveComposerTrigger = useCallback((): {
    snapshot: { value: string; cursor: number; expandedCursor: number };
    trigger: ComposerTrigger | null;
  } => {
    const snapshot = readComposerSnapshot();
    return {
      snapshot,
      trigger: detectComposerTriggerForView(snapshot.value, snapshot.expandedCursor),
    };
  }, [detectComposerTriggerForView, readComposerSnapshot]);

  const onSelectComposerItem = useCallback(
    (item: ComposerCommandItem) => {
      if (composerSelectLockRef.current) return;
      composerSelectLockRef.current = true;
      window.requestAnimationFrame(() => {
        composerSelectLockRef.current = false;
      });
      const { snapshot, trigger } = resolveActiveComposerTrigger();
      if (!trigger) return;
      if (item.type === "path") {
        const replacement = `@${item.path} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "slash-command") {
        if (item.command === "model") {
          const replacement = "/model ";
          const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
            snapshot.value,
            trigger.rangeEnd,
            replacement,
          );
          const applied = applyPromptReplacement(
            trigger.rangeStart,
            replacementRangeEnd,
            replacement,
            { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
          );
          if (applied) {
            setComposerHighlightedItemId(null);
          }
          return;
        }
        void handleInteractionModeChange(item.command === "plan" ? "plan" : "default");
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
        });
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "skill") {
        if (!item.availableInSession) return;
        const replacement = `$${item.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      onProviderModelSelect(item.provider, item.model);
      const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
        expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
      });
      if (applied) {
        setComposerHighlightedItemId(null);
      }
    },
    [
      applyPromptReplacement,
      setComposerHighlightedItemId,
      handleInteractionModeChange,
      onProviderModelSelect,
      resolveActiveComposerTrigger,
    ],
  );
  const onComposerMenuItemHighlighted = useCallback((itemId: string | null) => {
    setComposerHighlightedItemId(itemId);
  }, []);
  const nudgeComposerMenuHighlight = useCallback(
    (key: "ArrowDown" | "ArrowUp") => {
      if (selectableComposerMenuItems.length === 0) {
        setComposerHighlightedItemId(null);
        return;
      }
      const highlightedIndex = selectableComposerMenuItems.findIndex(
        (item) => item.id === composerHighlightedItemId,
      );
      const normalizedIndex =
        highlightedIndex >= 0 ? highlightedIndex : key === "ArrowDown" ? -1 : 0;
      const offset = key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (normalizedIndex + offset + selectableComposerMenuItems.length) %
        selectableComposerMenuItems.length;
      const nextItem = selectableComposerMenuItems[nextIndex];
      setComposerHighlightedItemId(nextItem?.id ?? null);
    },
    [composerHighlightedItemId, selectableComposerMenuItems],
  );
  const isComposerMenuLoading =
    composerTriggerKind === "path"
      ? (pathTriggerQuery.length > 0 && composerPathQueryDebouncer.state.isPending) ||
        workspaceEntriesQuery.isLoading ||
        workspaceEntriesQuery.isFetching
      : (composerTriggerKind === "slash-command" || composerTriggerKind === "skill") &&
        operatorsMode &&
        selectedProvider === "codex" &&
        (isServerThread ? sessionSkillsQuery.isLoading : skillsLibraryQuery.isLoading);

  const onPromptChange = useCallback(
    (
      nextPrompt: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
    ) => {
      if (activePendingProgress?.activeQuestion && activePendingUserInput) {
        onChangeActivePendingUserInputCustomAnswer(
          activePendingProgress.activeQuestion.id,
          nextPrompt,
          nextCursor,
          expandedCursor,
          cursorAdjacentToMention,
        );
        return;
      }
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTriggerForView(nextPrompt, expandedCursor),
      );
    },
    [
      activePendingProgress?.activeQuestion,
      activePendingUserInput,
      detectComposerTriggerForView,
      onChangeActivePendingUserInputCustomAnswer,
      setPrompt,
    ],
  );

  const onSpeechTranscript = useCallback(
    (transcript: string) => {
      const anchor = speechDictationAnchorRef.current;
      if (!anchor || anchor.threadId !== threadId) return;

      const insertion = insertDictatedTranscript(anchor.value, anchor.expandedCursor, transcript);
      if (!insertion) return;

      onPromptChange(
        insertion.value,
        collapseExpandedComposerCursor(insertion.value, insertion.expandedCursor),
        insertion.expandedCursor,
        false,
      );
    },
    [onPromptChange, threadId],
  );
  const onSpeechError = useCallback(
    (description: string) => {
      speechDictationAnchorRef.current = null;
      toastManager.add({
        type: "error",
        title: "Dictation unavailable",
        description,
        data: { threadId },
      });
    },
    [threadId],
  );
  const onSpeechLanguagePackInstalled = useCallback(
    (language: string) => {
      speechDictationAnchorRef.current = null;
      toastManager.add({
        type: "success",
        title: "Dictation ready",
        description: `Chrome installed the ${language} speech model on this device. Click the microphone again to start dictation.`,
        data: { threadId },
      });
    },
    [threadId],
  );
  const speechDictation = useChromeSpeechRecognition({
    enabled: !isArchivedThread && !isConnecting && !isComposerApprovalState && !isSendBusy,
    scopeKey: threadId,
    onTranscript: onSpeechTranscript,
    onError: onSpeechError,
    onLanguagePackInstalled: onSpeechLanguagePackInstalled,
  });
  const { phase: speechDictationPhase, toggle: toggleSpeechDictationRecognition } = speechDictation;
  const isSpeechDictationBusy = speechDictationPhase !== "idle";
  const isComposerSubmissionDisabled =
    isArchivedThread || isConnecting || isSendBusy || isSpeechDictationBusy;
  useEffect(() => {
    if (speechDictationPhase === "idle") speechDictationAnchorRef.current = null;
  }, [speechDictationPhase]);
  const toggleSpeechDictation = useCallback(() => {
    if (speechDictationPhase === "idle") {
      const snapshot = composerEditorRef.current?.readSnapshot();
      if (!snapshot) return;
      speechDictationAnchorRef.current = {
        value: snapshot.value,
        expandedCursor: snapshot.expandedCursor,
        threadId,
      };
    }
    toggleSpeechDictationRecognition();
  }, [speechDictationPhase, threadId, toggleSpeechDictationRecognition]);

  const onComposerCommandKey = (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
  ) => {
    if (key === "Tab" && event.shiftKey) {
      toggleInteractionMode();
      return true;
    }

    const { trigger } = resolveActiveComposerTrigger();
    const menuIsActive = composerMenuOpenRef.current || trigger !== null;

    if (menuIsActive) {
      const currentItems = composerMenuItemsRef.current;
      if (key === "ArrowDown" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowDown");
        return true;
      }
      if (key === "ArrowUp" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowUp");
        return true;
      }
      if (key === "Tab" || key === "Enter") {
        const selectedItem =
          activeComposerMenuItemRef.current ??
          currentItems.find((item) => item.type !== "skill" || item.availableInSession);
        if (selectedItem) {
          onSelectComposerItem(selectedItem);
          return true;
        }
      }
    }

    if (key === "Enter" && !event.shiftKey) {
      void onSend();
      return true;
    }
    return false;
  };
  const onToggleWorkGroup = useCallback((groupId: string) => {
    setExpandedWorkGroups((existing) => ({
      ...existing,
      [groupId]: !existing[groupId],
    }));
  }, []);
  const onToggleTurnTimingDivider = useCallback((dividerId: string) => {
    setExpandedCompletedTurnKeys((existing) => ({
      ...existing,
      [dividerId]: !existing[dividerId],
    }));
  }, []);
  const onExpandTimelineImage = useCallback((preview: ExpandedImagePreview) => {
    setExpandedImage(preview);
  }, []);
  const expandedImageItem = expandedImage ? expandedImage.images[expandedImage.index] : null;
  const onOpenTurnDiff = useCallback(
    (turnId: TurnId, filePath?: string) => {
      void navigate({
        to: "/operators/$threadId",
        params: { threadId },
        search: (previous) => {
          const rest = stripDiffSearchParams(previous);
          return filePath
            ? { ...rest, diff: "1", diffTurnId: turnId, diffFilePath: filePath }
            : { ...rest, diff: "1", diffTurnId: turnId };
        },
      });
    },
    [navigate, threadId],
  );
  const onRevertUserMessage = (messageId: MessageId) => {
    const targetTurnCount = revertTurnCountByUserMessageId.get(messageId);
    if (typeof targetTurnCount !== "number") {
      return;
    }
    void onRevertToTurnCount(targetTurnCount);
  };

  // Empty state: no active thread
  if (!activeThread) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-muted-foreground/40">
        {!isElectron && (
          <header className="border-b border-border px-3 py-2">
            <div className="flex items-center gap-2">
              <SidebarCollapsedTrigger className="size-7 shrink-0" />
              <span className="text-sm font-medium text-foreground">Threads</span>
            </div>
          </header>
        )}
        {isElectron && (
          <div className="drag-region flex h-[52px] shrink-0 items-center border-b border-border px-5">
            <span className="text-xs text-muted-foreground/50">No active thread</span>
          </div>
        )}
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <p className="text-sm">Select a thread or create a new one to get started.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
      {/* Top bar */}
      <header
        className={cn(
          "border-b border-border px-3 sm:px-5",
          isElectron
            ? operatorsMode
              ? "drag-region flex min-h-[52px] items-center py-2"
              : "drag-region flex h-[52px] items-center"
            : "py-2 sm:py-3",
        )}
      >
        <ChatHeader
          activeThreadId={activeThread.id}
          activeThreadTitle={activeThread.title}
          activeOperatorsSession={activeOperatorsSession}
          activeProjectName={activeProject?.name}
          isGitRepo={isGitRepo}
          openInCwd={activeThread.worktreePath ?? activeProject?.cwd ?? null}
          activeProjectScripts={activeProject?.scripts}
          preferredScriptId={
            activeProject ? (lastInvokedScriptByProjectId[activeProject.id] ?? null) : null
          }
          keybindings={keybindings}
          availableEditors={availableEditors}
          diffToggleShortcutLabel={diffPanelShortcutLabel}
          gitCwd={gitCwd}
          diffOpen={diffOpen}
          showDiffToggle={showCodeModeSurfaces}
          onRunProjectScript={(script) => {
            void runProjectScript(script);
          }}
          onAddProjectScript={saveProjectScript}
          onUpdateProjectScript={updateProjectScript}
          onDeleteProjectScript={deleteProjectScript}
          onToggleDiff={onToggleDiff}
        />
      </header>

      {/* Error banner */}
      <ProviderHealthBanner status={activeProviderStatus} />
      <ThreadErrorBanner
        error={activeThread.error}
        onDismiss={() => setThreadError(activeThread.id, null)}
      />
      {/* Main content area with optional plan sidebar */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {/* Chat column */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Messages Wrapper */}
          <div className="relative flex min-h-0 flex-1 flex-col">
            {/* Messages */}
            <div
              ref={setMessagesScrollContainerRef}
              className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-3 py-3 sm:px-5 sm:py-4"
              onScroll={onMessagesScroll}
              onClickCapture={onMessagesClickCapture}
              onWheel={onMessagesWheel}
              onPointerDown={onMessagesPointerDown}
              onPointerUp={onMessagesPointerUp}
              onPointerCancel={onMessagesPointerCancel}
              onTouchStart={onMessagesTouchStart}
              onTouchMove={onMessagesTouchMove}
              onTouchEnd={onMessagesTouchEnd}
              onTouchCancel={onMessagesTouchEnd}
            >
              <MessagesTimeline
                threadId={threadId}
                key={activeThread.id}
                hasMessages={visibleTimelineEntries.length > 0}
                isWorking={isWorking}
                activeTurnInProgress={activeTurnInProgress}
                showThinkingIndicator={showThinkingIndicator}
                activeTurnStartedAt={activeWorkStartedAt}
                scrollContainer={messagesScrollElement}
                timelineEntries={visibleTimelineEntries}
                turnTimingDividers={turnTimingDividers}
                onToggleTurnTimingDivider={onToggleTurnTimingDivider}
                turnDiffSummaryByAssistantMessageId={turnDiffSummaryByAssistantMessageId}
                nowIso={nowIso}
                expandedWorkGroups={expandedWorkGroups}
                onToggleWorkGroup={onToggleWorkGroup}
                onOpenTurnDiff={onOpenTurnDiff}
                revertTurnCountByUserMessageId={revertTurnCountByUserMessageId}
                onRevertUserMessage={onRevertUserMessage}
                isRevertingCheckpoint={isRevertingCheckpoint}
                onImageExpand={onExpandTimelineImage}
                markdownCwd={gitCwd ?? undefined}
                onWorkspaceFileLinkClick={onWorkspaceFileLinkClick}
                resolvedTheme={resolvedTheme}
                timestampFormat={timestampFormat}
                showToolActivity={showCodeModeSurfaces}
                showTurnDiffs={showCodeModeSurfaces}
                workspaceRoot={activeProject?.cwd ?? undefined}
              />
            </div>

            {/* scroll to bottom pill — shown when user has scrolled away from the bottom */}
            {showScrollToBottom && (
              <div className="pointer-events-none absolute bottom-1 left-1/2 z-30 flex -translate-x-1/2 justify-center py-1.5">
                <button
                  type="button"
                  onClick={() => scrollMessagesToBottom("smooth")}
                  className="pointer-events-auto flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-3 py-1 text-muted-foreground text-xs shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  <ChevronDownIcon className="size-3.5" />
                  Scroll to bottom
                </button>
              </div>
            )}
          </div>

          {/* Input bar */}
          <div className={cn("px-3 pt-1.5 sm:px-5 sm:pt-2", isGitRepo ? "pb-1" : "pb-3 sm:pb-4")}>
            <form
              ref={composerFormRef}
              onSubmit={onSend}
              className="mx-auto w-full min-w-0 max-w-3xl"
              data-chat-composer-form="true"
            >
              <input
                ref={composerFileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={onComposerFileInputChange}
              />
              <div
                className={`group rounded-[20px] border bg-card transition-colors duration-200 focus-within:border-ring/45 ${
                  isDragOverComposer ? "border-primary/70 bg-accent/30" : "border-border"
                }`}
                onDragEnter={onComposerDragEnter}
                onDragOver={onComposerDragOver}
                onDragLeave={onComposerDragLeave}
                onDrop={onComposerDrop}
              >
                {activePendingApproval ? (
                  <div className="rounded-t-[19px] border-b border-border/65 bg-muted/20">
                    <ComposerPendingApprovalPanel
                      approval={activePendingApproval}
                      pendingCount={pendingApprovals.length}
                    />
                  </div>
                ) : pendingUserInputs.length > 0 ? (
                  <div className="rounded-t-[19px] border-b border-border/65 bg-muted/20">
                    <ComposerPendingUserInputPanel
                      pendingUserInputs={pendingUserInputs}
                      respondingRequestIds={respondingRequestIds}
                      answers={activePendingDraftAnswers}
                      disabled={isArchivedThread}
                      questionIndex={activePendingQuestionIndex}
                      onSelectOption={onSelectActivePendingUserInputOption}
                      onAdvance={onAdvanceActivePendingUserInput}
                    />
                  </div>
                ) : showPlanFollowUpPrompt && activeProposedPlan ? (
                  <div className="rounded-t-[19px] border-b border-border/65 bg-muted/20">
                    <ComposerPlanFollowUpBanner
                      key={activeProposedPlan.id}
                      planTitle={proposedPlanTitle(activeProposedPlan.planMarkdown) ?? null}
                    />
                  </div>
                ) : null}

                {/* Textarea area */}
                <div
                  className={cn(
                    "relative px-3 pb-2 sm:px-4",
                    hasComposerHeader ? "pt-2.5 sm:pt-3" : "pt-3.5 sm:pt-4",
                  )}
                >
                  {composerMenuOpen && !isComposerApprovalState && (
                    <div className="absolute inset-x-0 bottom-full z-20 mb-2 px-1">
                      <ComposerCommandMenu
                        items={composerMenuItems}
                        resolvedTheme={resolvedTheme}
                        isLoading={isComposerMenuLoading}
                        triggerKind={composerTriggerKind}
                        activeItemId={activeComposerMenuItem?.id ?? null}
                        onHighlightedItemChange={onComposerMenuItemHighlighted}
                        onSelect={onSelectComposerItem}
                      />
                    </div>
                  )}

                  {!isComposerApprovalState &&
                    pendingUserInputs.length === 0 &&
                    (composerImages.length > 0 || composerFiles.length > 0) && (
                      <div className="mb-3 space-y-2">
                        {composerImages.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {composerImages.map((image) => (
                              <div
                                key={image.id}
                                className="relative h-16 w-16 overflow-hidden rounded-lg border border-border/80 bg-background"
                              >
                                {image.previewUrl ? (
                                  <button
                                    type="button"
                                    className="h-full w-full cursor-zoom-in"
                                    aria-label={`Preview ${image.name}`}
                                    onClick={() => {
                                      const preview = buildExpandedImagePreview(
                                        composerImages,
                                        image.id,
                                      );
                                      if (!preview) return;
                                      setExpandedImage(preview);
                                    }}
                                  >
                                    <img
                                      src={image.previewUrl}
                                      alt={image.name}
                                      className="h-full w-full object-cover"
                                    />
                                  </button>
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-muted-foreground/70">
                                    {image.name}
                                  </div>
                                )}
                                {nonPersistedComposerImageIdSet.has(image.id) && (
                                  <Tooltip>
                                    <TooltipTrigger
                                      render={
                                        <span
                                          role="img"
                                          aria-label="Draft attachment may not persist"
                                          className="absolute left-1 top-1 inline-flex items-center justify-center rounded bg-background/85 p-0.5 text-amber-600"
                                        >
                                          <CircleAlertIcon className="size-3" />
                                        </span>
                                      }
                                    />
                                    <TooltipPopup
                                      side="top"
                                      className="max-w-64 whitespace-normal leading-tight"
                                    >
                                      Draft attachment could not be saved locally and may be lost on
                                      navigation.
                                    </TooltipPopup>
                                  </Tooltip>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon-xs"
                                  className="absolute right-1 top-1 bg-background/80 hover:bg-background/90"
                                  onClick={() => removeComposerImage(image.id)}
                                  aria-label={`Remove ${image.name}`}
                                >
                                  <XIcon />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        {composerFiles.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {composerFiles.map((file) => (
                              <div
                                key={file.id}
                                className="relative flex max-w-full items-center gap-2 rounded-full border border-border/80 bg-background px-3 py-2 pr-9"
                              >
                                <FileIcon className="size-4 shrink-0 text-muted-foreground/70" />
                                <div className="min-w-0">
                                  <p className="truncate text-sm text-foreground" title={file.name}>
                                    {file.name}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground/60">
                                    {formatComposerFileSize(file.sizeBytes)}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon-xs"
                                  className="absolute right-1 top-1/2 -translate-y-1/2"
                                  onClick={() => removeComposerFile(file.id)}
                                  aria-label={`Remove ${file.name}`}
                                >
                                  <XIcon />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  <ComposerPromptEditor
                    ref={composerEditorRef}
                    value={
                      isComposerApprovalState
                        ? ""
                        : activePendingProgress
                          ? activePendingProgress.customAnswer
                          : prompt
                    }
                    cursor={composerCursor}
                    onChange={onPromptChange}
                    onCommandKeyDown={onComposerCommandKey}
                    onPaste={onComposerPaste}
                    placeholder={
                      isComposerApprovalState
                        ? (activePendingApproval?.detail ??
                          "Resolve this approval request to continue")
                        : activePendingProgress
                          ? "Type your own answer, or leave this blank to use the selected option"
                          : showPlanFollowUpPrompt && activeProposedPlan
                            ? "Add feedback to refine the plan, or leave this blank to implement it"
                            : isArchivedThread
                              ? "Archived threads are read-only"
                              : phase === "disconnected"
                                ? operatorsMode
                                  ? "Ask for follow-up changes or attach files and images"
                                  : "Ask for follow-up changes or attach images"
                                : operatorsMode
                                  ? "Ask anything or use / to show available commands"
                                  : "Ask anything, @tag files/folders, or use / to show available commands"
                    }
                    disabled={
                      isArchivedThread ||
                      isConnecting ||
                      isComposerApprovalState ||
                      isSendBusy ||
                      isSpeechDictationBusy
                    }
                  />
                </div>

                {/* Bottom toolbar */}
                {activePendingApproval ? (
                  <div className="flex items-center justify-end gap-2 px-2.5 pb-2.5 sm:px-3 sm:pb-3">
                    <ComposerPendingApprovalActions
                      disabled={isArchivedThread}
                      requestId={activePendingApproval.requestId}
                      isResponding={respondingRequestIds.includes(activePendingApproval.requestId)}
                      onRespondToApproval={onRespondToApproval}
                    />
                  </div>
                ) : (
                  <div
                    data-chat-composer-footer="true"
                    className={cn(
                      "flex items-center justify-between px-2.5 pb-2.5 sm:px-3 sm:pb-3",
                      isComposerFooterCompact
                        ? "gap-1.5"
                        : "flex-wrap gap-2 sm:flex-nowrap sm:gap-0",
                    )}
                  >
                    <div
                      className={cn(
                        "flex min-w-0 flex-1 items-center",
                        isComposerFooterCompact
                          ? "gap-1 overflow-hidden"
                          : "gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:min-w-max sm:overflow-visible",
                      )}
                    >
                      {/* Provider/model picker */}
                      {operatorsMode ? (
                        <OperatorsSurfaceToggle
                          value={settings.operatorsSurface}
                          onChange={(operatorsSurface) => updateSettings({ operatorsSurface })}
                        />
                      ) : (
                        <ProviderModelPicker
                          compact={isComposerFooterCompact}
                          disabled={isArchivedThread}
                          provider={selectedProvider}
                          model={selectedModelForPickerWithCustomFallback}
                          modelOptionsByProvider={modelOptionsByProvider}
                          onProviderModelChange={onProviderModelSelect}
                        />
                      )}

                      {isComposerFooterCompact ? (
                        <CompactComposerControlsMenu
                          activePlan={Boolean(activePlan || activeProposedPlan || planSidebarOpen)}
                          disabled={isArchivedThread}
                          interactionMode={interactionMode}
                          planSidebarOpen={planSidebarOpen}
                          selectedEffort={selectedEffort}
                          recommendedEffort={recommendedEffort}
                          selectedProvider={selectedProvider}
                          selectedCodexFastModeEnabled={selectedCodexFastModeEnabled}
                          reasoningOptions={reasoningOptions}
                          onEffortSelect={onEffortSelect}
                          onCodexFastModeChange={onCodexFastModeChange}
                          onToggleInteractionMode={toggleInteractionMode}
                          onTogglePlanSidebar={togglePlanSidebar}
                        />
                      ) : (
                        <>
                          {selectedProvider === "codex" && selectedEffort != null ? (
                            <>
                              <Separator
                                orientation="vertical"
                                className="mx-0.5 hidden h-4 sm:block"
                              />
                              <CodexTraitsPicker
                                disabled={isArchivedThread}
                                effort={selectedEffort}
                                recommendedEffort={recommendedEffort}
                                fastModeEnabled={selectedCodexFastModeEnabled}
                                options={reasoningOptions}
                                onEffortChange={onEffortSelect}
                                onFastModeChange={onCodexFastModeChange}
                              />
                            </>
                          ) : null}

                          <Separator
                            orientation="vertical"
                            className="mx-0.5 hidden h-4 sm:block"
                          />

                          <Button
                            variant="ghost"
                            className="shrink-0 whitespace-nowrap px-2 text-muted-foreground/70 hover:text-foreground/80 sm:px-3"
                            size="sm"
                            type="button"
                            onClick={toggleInteractionMode}
                            disabled={isArchivedThread}
                            title={
                              interactionMode === "plan"
                                ? "Plan mode — click to return to normal chat mode"
                                : "Default mode — click to enter plan mode"
                            }
                          >
                            <BotIcon />
                            <span className="sr-only sm:not-sr-only">
                              {interactionMode === "plan" ? "Plan" : "Chat"}
                            </span>
                          </Button>

                          <Separator
                            orientation="vertical"
                            className="mx-0.5 hidden h-4 sm:block"
                          />

                          {activePlan || activeProposedPlan || planSidebarOpen ? (
                            <>
                              <Separator
                                orientation="vertical"
                                className="mx-0.5 hidden h-4 sm:block"
                              />
                              <Button
                                variant="ghost"
                                className={cn(
                                  "shrink-0 whitespace-nowrap px-2 sm:px-3",
                                  planSidebarOpen
                                    ? "text-blue-400 hover:text-blue-300"
                                    : "text-muted-foreground/70 hover:text-foreground/80",
                                )}
                                size="sm"
                                type="button"
                                onClick={togglePlanSidebar}
                                disabled={isArchivedThread}
                                title={planSidebarOpen ? "Hide plan sidebar" : "Show plan sidebar"}
                              >
                                <ListTodoIcon />
                                <span className="sr-only sm:not-sr-only">Plan</span>
                              </Button>
                            </>
                          ) : null}
                        </>
                      )}
                    </div>

                    {/* Right side: send / stop button */}
                    <div
                      data-chat-composer-actions="right"
                      className="flex shrink-0 items-center gap-2"
                    >
                      {operatorsMode ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="rounded-full px-3 text-muted-foreground/70 hover:text-foreground/80"
                          onClick={openComposerFilePicker}
                          disabled={isArchivedThread || isSendBusy || isConnecting}
                          title="Upload files to the session workspace"
                        >
                          <PaperclipIcon className="size-4" />
                          <span className="sr-only sm:not-sr-only">Files</span>
                        </Button>
                      ) : null}
                      {operatorsMode && speechDictation.isChrome ? (
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          className={cn(
                            "rounded-full text-muted-foreground/70 hover:text-foreground/80",
                            speechDictation.phase === "listening" &&
                              "bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 hover:text-rose-600 dark:text-rose-400 dark:hover:text-rose-300",
                          )}
                          onClick={toggleSpeechDictation}
                          disabled={
                            isArchivedThread ||
                            isConnecting ||
                            isComposerApprovalState ||
                            isSendBusy ||
                            speechDictation.phase === "stopping"
                          }
                          aria-label={
                            speechDictation.phase === "listening"
                              ? "Stop dictation"
                              : speechDictation.phase === "preparing"
                                ? "Cancel dictation setup"
                                : speechDictation.phase === "stopping"
                                  ? "Stopping dictation"
                                  : "Start dictation"
                          }
                          aria-pressed={
                            speechDictation.phase === "listening" ||
                            speechDictation.phase === "stopping"
                          }
                          title={
                            speechDictation.phase === "listening"
                              ? "Stop dictation"
                              : speechDictation.phase === "preparing"
                                ? "Cancel dictation setup"
                                : speechDictation.phase === "stopping"
                                  ? "Stopping dictation"
                                  : "Start on-device dictation"
                          }
                        >
                          {speechDictation.phase === "preparing" ||
                          speechDictation.phase === "stopping" ? (
                            <LoaderCircleIcon className="size-4 animate-spin" />
                          ) : (
                            <MicIcon className="size-4" />
                          )}
                        </Button>
                      ) : null}
                      {isPreparingWorktree ? (
                        <span className="text-muted-foreground/70 text-xs">
                          Preparing worktree...
                        </span>
                      ) : null}
                      {activePendingProgress ? (
                        <div className="flex items-center gap-2">
                          {activePendingProgress.questionIndex > 0 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full"
                              onClick={onPreviousActivePendingUserInputQuestion}
                              disabled={isComposerSubmissionDisabled || activePendingIsResponding}
                            >
                              Previous
                            </Button>
                          ) : null}
                          <Button
                            type="submit"
                            size="sm"
                            className="rounded-full px-4"
                            disabled={
                              isComposerSubmissionDisabled ||
                              activePendingIsResponding ||
                              (activePendingProgress.isLastQuestion
                                ? !activePendingResolvedAnswers
                                : !activePendingProgress.canAdvance)
                            }
                          >
                            {activePendingIsResponding
                              ? "Submitting..."
                              : activePendingProgress.isLastQuestion
                                ? "Submit answers"
                                : "Next question"}
                          </Button>
                        </div>
                      ) : phase === "running" && !isArchivedThread ? (
                        <button
                          type="button"
                          className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-rose-500/90 text-white transition-all duration-150 hover:bg-rose-500 hover:scale-105 sm:h-8 sm:w-8"
                          onClick={() => void onInterrupt()}
                          aria-label="Stop generation"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <rect x="2" y="2" width="8" height="8" rx="1.5" />
                          </svg>
                        </button>
                      ) : pendingUserInputs.length === 0 ? (
                        showPlanFollowUpPrompt ? (
                          prompt.trim().length > 0 ? (
                            <Button
                              type="submit"
                              size="sm"
                              className="h-9 rounded-full px-4 sm:h-8"
                              disabled={isComposerSubmissionDisabled}
                            >
                              {isConnecting || isSendBusy ? "Sending..." : "Refine"}
                            </Button>
                          ) : (
                            <div className="flex items-center">
                              <Button
                                type="submit"
                                size="sm"
                                className="h-9 rounded-l-full rounded-r-none px-4 sm:h-8"
                                disabled={isComposerSubmissionDisabled}
                              >
                                {isConnecting || isSendBusy ? "Sending..." : "Implement"}
                              </Button>
                              <Menu>
                                <MenuTrigger
                                  render={
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="h-9 rounded-l-none rounded-r-full border-l-white/12 px-2 sm:h-8"
                                      aria-label="Implementation actions"
                                      disabled={isComposerSubmissionDisabled}
                                    />
                                  }
                                >
                                  <ChevronDownIcon className="size-3.5" />
                                </MenuTrigger>
                                <MenuPopup align="end" side="top">
                                  <MenuItem
                                    disabled={isComposerSubmissionDisabled}
                                    onClick={() => void onImplementPlanInNewThread()}
                                  >
                                    Implement in a new thread
                                  </MenuItem>
                                </MenuPopup>
                              </Menu>
                            </div>
                          )
                        ) : (
                          <button
                            type="submit"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/90 text-primary-foreground transition-all duration-150 hover:bg-primary hover:scale-105 disabled:opacity-30 disabled:hover:scale-100 sm:h-8 sm:w-8"
                            disabled={
                              isComposerSubmissionDisabled ||
                              (!prompt.trim() &&
                                composerImages.length === 0 &&
                                composerFiles.length === 0)
                            }
                            aria-label={
                              isConnecting
                                ? "Connecting"
                                : isPreparingWorktree
                                  ? "Preparing worktree"
                                  : isSendBusy
                                    ? "Sending"
                                    : "Send message"
                            }
                          >
                            {isConnecting || isSendBusy ? (
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                className="animate-spin"
                                aria-hidden="true"
                              >
                                <circle
                                  cx="7"
                                  cy="7"
                                  r="5.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeDasharray="20 12"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                aria-hidden="true"
                              >
                                <path
                                  d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </button>
                        )
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          {!operatorsMode && isGitRepo && (
            <BranchToolbar
              threadId={activeThread.id}
              onEnvModeChange={onEnvModeChange}
              envLocked={envLocked}
              onComposerFocusRequest={scheduleComposerFocus}
              {...(canCheckoutPullRequestIntoThread
                ? { onCheckoutPullRequestRequest: openPullRequestDialog }
                : {})}
            />
          )}
          {pullRequestDialogState ? (
            <PullRequestThreadDialog
              key={pullRequestDialogState.key}
              open
              cwd={activeProject?.cwd ?? null}
              initialReference={pullRequestDialogState.initialReference}
              onOpenChange={(open) => {
                if (!open) {
                  closePullRequestDialog();
                }
              }}
              onPrepared={handlePreparedPullRequestThread}
            />
          ) : null}
        </div>
        {/* end chat column */}

        {/* Plan sidebar */}
        {planSidebarOpen ? (
          <PlanSidebar
            activePlan={activePlan}
            activeProposedPlan={activeProposedPlan}
            markdownCwd={gitCwd ?? undefined}
            onWorkspaceFileLinkClick={onWorkspaceFileLinkClick}
            workspaceRoot={operatorsMode ? undefined : (activeProject?.cwd ?? undefined)}
            timestampFormat={timestampFormat}
            onClose={() => {
              setPlanSidebarOpen(false);
              // Track that the user explicitly dismissed for this turn so auto-open won't fight them.
              const turnKey = activePlan?.turnId ?? activeProposedPlan?.turnId ?? null;
              if (turnKey) {
                planSidebarDismissedForTurnRef.current = turnKey;
              }
            }}
          />
        ) : null}
      </div>
      {/* end horizontal flex container */}

      {(() => {
        if (operatorsMode || !terminalState.terminalOpen || !activeProject) {
          return null;
        }
        return (
          <ThreadTerminalDrawer
            key={activeThread.id}
            threadId={activeThread.id}
            cwd={gitCwd ?? activeProject.cwd}
            runtimeEnv={threadTerminalRuntimeEnv}
            height={terminalState.terminalHeight}
            terminalIds={terminalState.terminalIds}
            activeTerminalId={terminalState.activeTerminalId}
            terminalGroups={terminalState.terminalGroups}
            activeTerminalGroupId={terminalState.activeTerminalGroupId}
            focusRequestId={terminalFocusRequestId}
            onSplitTerminal={splitTerminal}
            onNewTerminal={createNewTerminal}
            splitShortcutLabel={splitTerminalShortcutLabel ?? undefined}
            newShortcutLabel={newTerminalShortcutLabel ?? undefined}
            closeShortcutLabel={closeTerminalShortcutLabel ?? undefined}
            onActiveTerminalChange={activateTerminal}
            onCloseTerminal={closeTerminal}
            onHeightChange={setTerminalHeight}
          />
        );
      })()}

      {expandedImage && expandedImageItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 py-6 [-webkit-app-region:no-drag]"
          role="dialog"
          aria-modal="true"
          aria-label="Expanded image preview"
        >
          <button
            type="button"
            className="absolute inset-0 z-0 cursor-zoom-out"
            aria-label="Close image preview"
            onClick={closeExpandedImage}
          />
          {expandedImage.images.length > 1 && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute left-2 top-1/2 z-20 -translate-y-1/2 text-white/90 hover:bg-white/10 hover:text-white sm:left-6"
              aria-label="Previous image"
              onClick={() => {
                navigateExpandedImage(-1);
              }}
            >
              <ChevronLeftIcon className="size-5" />
            </Button>
          )}
          <div className="relative isolate z-10 max-h-[92vh] max-w-[92vw]">
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              className="absolute right-2 top-2"
              onClick={closeExpandedImage}
              aria-label="Close image preview"
            >
              <XIcon />
            </Button>
            <img
              src={expandedImageItem.src}
              alt={expandedImageItem.name}
              className="max-h-[86vh] max-w-[92vw] select-none rounded-lg border border-border/70 bg-background object-contain shadow-2xl"
              draggable={false}
            />
            <p className="mt-2 max-w-[92vw] truncate text-center text-xs text-muted-foreground/80">
              {expandedImageItem.name}
              {expandedImage.images.length > 1
                ? ` (${expandedImage.index + 1}/${expandedImage.images.length})`
                : ""}
            </p>
          </div>
          {expandedImage.images.length > 1 && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute right-2 top-1/2 z-20 -translate-y-1/2 text-white/90 hover:bg-white/10 hover:text-white sm:right-6"
              aria-label="Next image"
              onClick={() => {
                navigateExpandedImage(1);
              }}
            >
              <ChevronRightIcon className="size-5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
